import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Table, Button, Modal, Form, Card } from 'react-bootstrap';
import { ReactComponent as EditIcon } from '../../../assets/images/Edit.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/images/Delete.svg';
import AddNewButton from '../../../Common/AddNewButton';
import Select from 'react-select';
// Updated API service with startTime
import { addFixture, editFixture, deleteFixture, getFixtureById, getPresignedUrl, bulkUploadFixtures, PresignedUrlRequest, PresignedUrlResponse, BulkUploadRequest } from '../../../api/tournament/fixturesService';
import { listTeams } from '../../../api/tournament/teamService';
import { listPlayers } from '../../../api/tournament/playerService';
import { getTeamStandings } from '../../../api/tournament/standingsService';
import { DeleteModal } from '../../../Common/DeleteModal';
import ShimmerLoader from '../../../Common/ShimmerLoader';
import { Fixture } from '../../../api/tournament/fixturesService';
import { TOURNAMENT_KNOCKOUT_STAGES } from '../../../api/tournament/types';
import {
  PICKLEBALL_EVENT_CATEGORIES,
  isPickleballDoubles,
} from '../../../api/tournament/pickleballFixture';
import '../../../Common/ManageTournamentsFixturesTable.css';
import TournamentBulkUploadModal from '../../../components/tournament/TournamentBulkUploadModal';

type SelectOption = { value: string; label: string };

interface FixturesTableProps {
  fixtures: Fixture[];
  search: string;
  onSearch: (value: string) => void;
  onEdit: () => void;
  onDelete: () => void;
  onAdd: () => void;
  ageCategories: { value: string; label: string; standingsType?: string; format?: string }[];
  tournamentId: string;
  fixturesLoading: boolean;
  page: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  ageCategoryFilter?: any;
  onAgeCategoryFilterChange?: (filter: any) => void;
  statusFilter?: { value: string; label: string } | null;
  onStatusFilterChange?: (filter: { value: string; label: string } | null) => void;
  initialOpenFixture?: { id: string; mode: 'view' | 'edit' } | null;
  navigateToDetailPage?: boolean; // If true, navigate to detail page instead of opening modal
  onDownloadExcel?: () => void; // Handler for downloading Excel
  isDownloadingExcel?: boolean; // Loading state for Excel download
  tournamentSeasonId?: string; // Optional: tournamentSeason id for season context
  tournamentSports?: string; // Add sport prop for API calls
}

  const stageOptions = [
    { value: 'league', label: 'League' },
    { value: 'knockout', label: 'Knockout' },
  ];

const statusOptions = [
  { value: '', label: 'All' },
  { value: 'started', label: 'Live' },
  { value: 'pending', label: 'Upcoming' },
  { value: 'paused', label: 'PAUSED' },
  { value: 'completed', label: 'COMPLETED' },
];

const PAGE_SIZE_OPTIONS = [10, 15, 20, 50];

// Helper function to convert 24-hour time to 12-hour format
const formatTimeTo12Hour = (time24: string) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${hour12.toString().padStart(2, '0')}:${minutes} ${ampm}`;
};

// Helper function to convert 12-hour time to 24-hour format
const formatTimeTo24Hour = (time12: string): string => {
  if (!time12) return '';
  
  // Handle both 12-hour format (08:00 PM) and already 24-hour format (20:00)
  if (time12.includes('AM') || time12.includes('PM')) {
    // 12-hour format: "08:00 PM" or "8:00 AM"
    const match = time12.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (match) {
      let hour = parseInt(match[1]);
      const minute = match[2];
      const amPm = match[3].toUpperCase();
      
      if (amPm === 'PM' && hour !== 12) {
        hour += 12;
      } else if (amPm === 'AM' && hour === 12) {
        hour = 0;
      }
      
      return `${hour.toString().padStart(2, '0')}:${minute}`;
    }
  } else {
    // Already in 24-hour format: "20:00"
    // Just validate and return as-is
    const match = time12.match(/^(\d{1,2}):(\d{2})$/);
    if (match) {
      return `${parseInt(match[1]).toString().padStart(2, '0')}:${match[2]}`;
    }
  }
  
  return '';
};

// Helper function to normalize status from API response
// Backend values: PENDING = 'pending', STARTED = 'started', PAUSED = 'paused', CANCELLED = 'cancelled', COMPLETED = 'completed'
const normalizeStatus = (status?: string) => {
  if (!status) return 'pending';
  const s = status.toLowerCase().trim();
  if (!s) return 'pending';
  
  // Map to the required status values - prioritize exact backend values
  if (s === 'pending') return 'pending';
  if (s === 'started') return 'started';
  if (s === 'paused') return 'paused';
  if (s === 'cancelled' || s === 'canceled') return 'cancelled';
  if (s === 'completed') return 'completed';
  
  // Fallback mappings for legacy/alternative values
  if (s === 'upcoming' || (s.includes('yet') && s.includes('start'))) return 'pending';
  if (s === 'start' || s === 'live' || s === 'ongoing' || s === 'running') return 'started';
  if (s === 'on-hold' || s === 'on_hold' || s === 'hold') return 'paused';
  if (s === 'cancel') return 'cancelled';
  if (s === 'ended' || s === 'complete' || s === 'finished') return 'completed';
  
  // Default to pending for unknown statuses
  return 'pending';
};

// Helper function to get status badge with proper styling
// Consistent with manage-tournaments-fixtures: using payment-badge class and same styling
const getStatusBadge = (status?: string) => {
  const normalized = normalizeStatus(status);
  
  // Map fixture statuses to match manage-tournaments-fixtures styling
  if (normalized === 'started') {
    return (
      <span 
        className="badge payment-badge" 
        style={{
          border: '1px solid',
          fontWeight: '500',
          padding: '0.375rem 0.75rem',
          borderRadius: '6px',
          backgroundColor: '#E6F1FF',
          color: '#006EFF',
          borderColor: '#006EFF',
          textTransform: 'capitalize'
        }}
      >
        live
      </span>
    );
  }
  
  if (normalized === 'paused') {
    return (
      <span 
        className="badge payment-badge" 
        style={{
          border: '1px solid',
          fontWeight: '500',
          padding: '0.375rem 0.75rem',
          borderRadius: '6px',
          backgroundColor: '#FFF5EC',
          color: '#FD9644',
          borderColor: '#FD9644',
          textTransform: 'capitalize'
        }}
      >
        paused
      </span>
    );
  }
  
  if (normalized === 'cancelled') {
    return (
      <span 
        className="badge payment-badge" 
        style={{
          border: '1px solid',
          fontWeight: '500',
          padding: '0.375rem 0.75rem',
          borderRadius: '6px',
          backgroundColor: '#FEE',
          color: '#DC3545',
          borderColor: '#DC3545',
          textTransform: 'capitalize'
        }}
      >
        cancelled
      </span>
    );
  }
  
  if (normalized === 'completed') {
    return (
      <span 
        className="badge payment-badge" 
        style={{
          border: '1px solid',
          fontWeight: '500',
          padding: '0.375rem 0.75rem',
          borderRadius: '6px',
          backgroundColor: '#E9FCF2',
          color: '#1B9E5C',
          borderColor: '#1B9E5C',
          textTransform: 'capitalize'
        }}
      >
        completed
      </span>
    );
  }
  
  // Default: pending/upcoming
  return (
    <span 
      className="badge payment-badge" 
      style={{
        border: '1px solid',
        fontWeight: '500',
        padding: '0.375rem 0.75rem',
        borderRadius: '6px',
        backgroundColor: '#FFF5EC',
        color: '#FD9644',
        borderColor: '#FD9644',
        textTransform: 'capitalize'
      }}
    >
      upcoming
    </span>
  );
};


// Time picker options
const hours = Array.from({ length: 12 }, (_, i) => ({ 
  value: String(i + 1).padStart(2, '0'), 
  label: String(i + 1) 
}));

// Generate all minute options (00-59)
const minutes = Array.from({ length: 60 }, (_, i) => ({
  value: i.toString().padStart(2, '0'),
  label: i.toString().padStart(2, '0')
}));

const amPmOptions = [
  { value: 'AM', label: 'AM' },
  { value: 'PM', label: 'PM' },
];

const toParticipantOption = (
  participant: any,
  options: SelectOption[]
): SelectOption | null => {
  const team = participant?.team;
  const player = participant?.players?.[0];
  const value = typeof team === 'object'
    ? team?._id
    : team || (typeof player === 'object' ? player?._id : player);
  if (!value) return null;

  return options.find((option) => option.value === value) || {
    value,
    label: typeof team === 'object'
      ? team.name || team.shortName || value
      : typeof player === 'object'
        ? player.name || value
        : value,
  };
};

const toParticipantOptions = (
  participant: any,
  options: SelectOption[]
): SelectOption[] =>
  (participant?.players || [])
    .map((player: any) => {
      const value = typeof player === 'object' ? player?._id : player;
      if (!value) return null;
      return options.find((option) => option.value === value) || {
        value,
        label: typeof player === 'object' ? player.name || value : value,
      };
    })
    .filter((player: SelectOption | null): player is SelectOption => Boolean(player));

const hydrateParticipants = (fixture: any, isFootball: boolean): any[] => {
  if (Array.isArray(fixture?.participants) && fixture.participants.length) {
    return fixture.participants;
  }

  if (isFootball) {
    return [
      { team: fixture?.teamA || fixture?.team1Info || fixture?.side1?.team },
      { team: fixture?.teamB || fixture?.team2Info || fixture?.side2?.team },
    ];
  }

  return [
    fixture?.side1 || {
      team: fixture?.teamA || fixture?.team1Info?._id,
      players: fixture?.team1Info?.players,
    },
    fixture?.side2 || {
      team: fixture?.teamB || fixture?.team2Info?._id,
      players: fixture?.team2Info?.players,
    },
  ];
};

const getParticipantName = (participant: any): string => {
  if (participant?.team) {
    return typeof participant.team === 'string'
      ? participant.team
      : participant.team.name || participant.team.shortName || '-';
  }
  if (participant?.players?.length) {
    return participant.players
      .map((player: any) => typeof player === 'string' ? player : player.name)
      .filter(Boolean)
      .join(', ') || '-';
  }
  return '-';
};

const FixturesTable: React.FC<FixturesTableProps> = ({
  fixtures,
  search,
  onSearch,
  onEdit,
  onDelete,
  onAdd,
  ageCategories,
  navigateToDetailPage = false,
  tournamentId,
  fixturesLoading,
  page,
  pageSize,
  totalPages,
  totalCount,
  onPageChange,
  onPageSizeChange,
  ageCategoryFilter,
  onAgeCategoryFilterChange,
  statusFilter,
  onStatusFilterChange,
  initialOpenFixture,
  onDownloadExcel,
  isDownloadingExcel = false,
  tournamentSeasonId,
  tournamentSports,
}) => {
  // Helper to get sport - use prop if provided, otherwise throw error
  const getSport = (): string => {
    if (tournamentSports) {
      return tournamentSports;
    }
    throw new Error('Tournament sport is required. Please ensure tournamentSports prop is passed to FixturesTable.');
  };

  const isFootball = () => getSportValue().replace(/[\s_-]/g, '').toLowerCase() === 'football';

  // Helper function to get selected age category format
  const getSelectedAgeCategoryFormat = (): string | null => {
    if (!form.ageCategory?.value || !ageCategories.length) return null;
    const ageCategoryObj = ageCategories.find((ac: any) => 
      ac._id === form.ageCategory.value || ac.value === form.ageCategory.value
    );
    return ageCategoryObj?.format || null;
  };

  // Helper function to check if age category format is 'player'
  const isPlayerFormat = (): boolean => {
    const format = getSelectedAgeCategoryFormat();
    return format?.toLowerCase() === 'player';
  };

  const isPadel = (): boolean => getSportValue().replace(/[\s_-]/g, '').toLowerCase() === 'padel';
  const isPickleball = (): boolean => getSportValue().replace(/[\s_-]/g, '').toLowerCase() === 'pickleball';
  const usesMultiPlayerSides = (eventCategory = form.eventCategory?.value): boolean =>
    isPadel() || (isPickleball() && isPickleballDoubles(eventCategory));

  const usesPlayerParticipants = (): boolean =>
    isPadel() || isPickleball() || (!isFootball() && isPlayerFormat());
  
  const navigate = useNavigate();
  
  const [showAddForm, setShowAddForm] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState(false);
  const [filteredTeams, setFilteredTeams] = useState<{ value: string; label: string }[]>([]);
  const [filteredPlayers, setFilteredPlayers] = useState<{ value: string; label: string }[]>([]);
  const [availableGroups, setAvailableGroups] = useState<{ value: string; label: string }[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [playersLoading, setPlayersLoading] = useState(false);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [pendingEditData, setPendingEditData] = useState<any>(null);
  
  // Helper function to get sport value from tournamentSports prop
  const getSportValue = (): string => {
    if (tournamentSports) {
      return tournamentSports;
    }
    // Fallback to Football if tournamentSports is not provided (for backward compatibility)
    return 'Football';
  };

  const fixtureContextQuery = () => {
    const params = new URLSearchParams(window.location.search);
    params.set('sport', getSportValue());
    if (tournamentSeasonId) params.set('seasonId', tournamentSeasonId);
    return `?${params.toString()}`;
  };

  const [form, setForm] = useState<any>({
    matchNum: '',
    sports: { value: getSportValue().toLowerCase(), label: getSportValue() }, // Use tournamentSports prop instead of hardcoded 'Football'
    stage: null,
    ageCategory: null,
    eventCategory: null,
    group: null,
    participants: [null, null],
    date: '',
    time: '',
    venue: '',
    venueLong: '',
    title: '',
    subtitle: '',
    sets: '', // For Badminton and Table Tennis - numeric, odd numbers only
    round: '', // For Badminton and Table Tennis - string field
    // Knockout fields for Football
    knockoutStage: null,
    knockoutIndex: '',
    knockoutMatchNum: '',
    streamingVideoUrl: '',
  });
  const [formTouched, setFormTouched] = useState<any>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [isFormModified, setIsFormModified] = useState(false);
  const [originalForm, setOriginalForm] = useState<any>(null);

  // Open in view/edit mode when instructed by parent (e.g., from Match Management tab)
  useEffect(() => {
    if (!initialOpenFixture || !fixtures || fixtures.length === 0) return;
    const idx = fixtures.findIndex(f => f._id === initialOpenFixture.id);
    if (idx >= 0) {
      if (initialOpenFixture.mode === 'view') {
        handleView(idx);
      } else {
        handleEdit(idx);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOpenFixture?.id, initialOpenFixture?.mode, fixtures]);
  const cardRef = useRef<HTMLDivElement>(null);

  // Bulk upload state
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadResponseData, setUploadResponseData] = useState<any>(null);
  const [showResponseData, setShowResponseData] = useState(false);
  const [showTournamentBulkUpload, setShowTournamentBulkUpload] = useState(false);

  // Custom time picker state
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [selectedHour, setSelectedHour] = useState('');
  const [selectedMinute, setSelectedMinute] = useState('');
  const [selectedAmPm, setSelectedAmPm] = useState('');
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const timePickerButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sport = getSportValue();
    let changed = params.get('sport') !== sport;
    params.set('sport', sport);
    if (tournamentSeasonId && params.get('seasonId') !== tournamentSeasonId) {
      params.set('seasonId', tournamentSeasonId);
      changed = true;
    }
    if (changed) {
      window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}?${params.toString()}${window.location.hash}`
      );
    }
  }, [tournamentSeasonId, tournamentSports]);

  // Helper functions for custom time picker
  const convertTo12Hour = (hour: string, minute: string, amPm: string) => {
    // Convert to 12-hour format for display and API
    const hour12 = hour.padStart(2, '0');
    const minute12 = minute.padStart(2, '0');
    return `${hour12}:${minute12} ${amPm}`;
  };

  const parseTimeToAmPm = (timeString: string) => {
    if (!timeString) return { hour: '', minute: '', amPm: '' };
    
    // Handle both 12-hour format (08:00 PM) and 24-hour format (20:00)
    if (timeString.includes('AM') || timeString.includes('PM')) {
      // 12-hour format: "08:00 PM"
      const match = timeString.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
      if (match) {
        return {
          hour: match[1].padStart(2, '0'),
          minute: match[2],
          amPm: match[3].toUpperCase()
        };
      }
    } else {
      // 24-hour format: "20:00"
      const [hours, mins] = timeString.split(':');
      const hour24 = parseInt(hours);
      const amPm = hour24 >= 12 ? 'PM' : 'AM';
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      return {
        hour: hour12.toString().padStart(2, '0'),
        minute: mins || '00',
        amPm: amPm
      };
    }
    
    return { hour: '', minute: '', amPm: '' };
  };

  const handleCustomTimeSelection = (type: 'hour' | 'minute' | 'amPm', value: string) => {
    if (type === 'hour') {
      setSelectedHour(value);
    } else if (type === 'minute') {
      setSelectedMinute(value);
    } else if (type === 'amPm') {
      setSelectedAmPm(value);
      
      // Auto-close and set time when AM/PM is selected
      if (selectedHour && selectedMinute) {
        const time12 = convertTo12Hour(selectedHour, selectedMinute, value);
        setForm({ ...form, time: time12 });
        setFormTouched({ ...formTouched, time: true });
        setShowTimePicker(false);
        // Reset selections
        setSelectedHour('');
        setSelectedMinute('');
        setSelectedAmPm('');
      }
    }
  };


  const openTimePicker = () => {
    if (timePickerButtonRef.current) {
      const rect = timePickerButtonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Fixed size for better display of all options
      const dropdownWidth = 600;
      const dropdownHeight = 500;
      
      // Center the dropdown in the viewport
      let top = (viewportHeight - dropdownHeight) / 2;
      let left = (viewportWidth - dropdownWidth) / 2;
      
      // Ensure it doesn't go off screen
      if (left < 20) {
        left = 20;
      }
      if (left + dropdownWidth > viewportWidth - 20) {
        left = viewportWidth - dropdownWidth - 20;
      }
      if (top < 20) {
        top = 20;
      }
      if (top + dropdownHeight > viewportHeight - 20) {
        top = viewportHeight - dropdownHeight - 20;
      }
      
      setPickerPosition({ top, left });
    }
    
    if (form.time) {
      const parsed = parseTimeToAmPm(form.time);
      setSelectedHour(parsed.hour);
      setSelectedMinute(parsed.minute);
      setSelectedAmPm(parsed.amPm);
    } else {
      setSelectedHour('');
      setSelectedMinute('');
      setSelectedAmPm('');
    }
    setShowTimePicker(true);
    
    // Prevent body scrolling
    document.body.classList.add('time-picker-open');
  };

  const closeTimePicker = () => {
    setShowTimePicker(false);
    setSelectedHour('');
    setSelectedMinute('');
    setSelectedAmPm('');
    
    // Re-enable body scrolling
    document.body.classList.remove('time-picker-open');
  };

  // Close time picker when clicking outside and handle scroll
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showTimePicker && !target.closest('.custom-time-picker-dropdown') && !target.closest('.time-picker-container')) {
        closeTimePicker();
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showTimePicker) {
        closeTimePicker();
      }
    };

    const handleScroll = () => {
      if (showTimePicker && timePickerButtonRef.current) {
        // Recalculate position on scroll
        const rect = timePickerButtonRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const dropdownWidth = Math.min(350, viewportWidth - 40);
        const dropdownHeight = 320;
        
        let newTop = rect.bottom + 8;
        let newLeft = rect.left;
        
        // Adjust horizontal position
        if (newLeft + dropdownWidth > viewportWidth - 20) {
          newLeft = viewportWidth - dropdownWidth - 20;
        }
        if (newLeft < 20) {
          newLeft = 20;
        }
        
        // Adjust vertical position based on available space
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;
        
        if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
          newTop = rect.top - dropdownHeight - 8;
        } else if (spaceBelow < dropdownHeight && spaceAbove < dropdownHeight) {
          newTop = (viewportHeight - dropdownHeight) / 2;
        }
        
        setPickerPosition({ top: newTop, left: newLeft });
      }
    };

    if (showTimePicker) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
      
      // Prevent body scroll on mobile when time picker is open
      if (window.innerWidth <= 768) {
        document.body.style.overflow = 'hidden';
      }
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      
      // Clean up body class when component unmounts
      document.body.classList.remove('time-picker-open');
    };
  }, [showTimePicker]);

  // Debounced search
  const [inputValue, setInputValue] = useState(search);
  useEffect(() => {
    const handler = setTimeout(() => {
      onSearch(inputValue);
    }, 300);
    return () => clearTimeout(handler);
  }, [inputValue, onSearch]);

  useEffect(() => {
    if (!showAddForm) {
      const sportValue = getSportValue();
      setForm({
        matchNum: '',
        sports: { value: sportValue.toLowerCase(), label: sportValue },
        stage: null,
        ageCategory: null,
        eventCategory: null,
        group: null,
        participants: [null, null],
        time: '',
        venue: '',
        venueLong: '',
        title: '',
        subtitle: '',
        sets: '',
        round: '',
        knockoutStage: null,
        knockoutIndex: '',
        knockoutMatchNum: '',
        streamingVideoUrl: '',
      });
      setFormTouched({});
      setFormError(null);
      setAvailableGroups([]);
      setFilteredTeams([]);
      setFilteredPlayers([]);
    }
  }, [showAddForm]);

  // Load groups based on age category and standings type
  const loadGroupsByAgeCategory = useCallback(async (ageCategoryId: string, standingsType: string): Promise<{ value: string; label: string }[]> => {
    if (!ageCategoryId || standingsType !== 'group') {
      setAvailableGroups([]);
      return [];
    }
    
    setGroupsLoading(true);
    try {
      const sport = getSportValue();
      const response = await getTeamStandings(ageCategoryId, sport, tournamentSeasonId);
      if (Array.isArray(response) && response.length > 0) {
        // Check if it's group standings (has groupName property)
        if (response[0].hasOwnProperty('groupName')) {
          const groupOptions = response.map((group: any) => ({
            value: group.groupName,
            label: `Group ${group.groupName}`
          }));
          setAvailableGroups(groupOptions);
          return groupOptions;
        } else {
          setAvailableGroups([]);
          return [];
        }
      } else {
        setAvailableGroups([]);
        return [];
      }
    } catch (error) {
      console.error('Error loading groups by age category:', error);
      setAvailableGroups([]);
      return [];
    } finally {
      setGroupsLoading(false);
    }
  }, [tournamentSeasonId, tournamentSports]);

  // Load teams based on selected age category and group
  const loadTeamsByAgeCategory = useCallback(async (ageCategoryId: string, groupName?: string) => {
    if (!ageCategoryId || !tournamentId) {
      setFilteredTeams([]);
      return;
    }
    
    // Find the age category object to get the label (name)
    const ageCategoryObj = ageCategories.find(ac => ac.value === ageCategoryId);
    if (!ageCategoryObj) {
      console.error('Age category object not found for ID:', ageCategoryId);
      setFilteredTeams([]);
      return;
    }
    
    const ageCategoryName = ageCategoryObj.label;
    
    setTeamsLoading(true);
    try {
      const sport = getSport();
      const response = await listTeams({
        tournamentId: tournamentId,
        ageCategory: ageCategoryName, // Use the label (name) instead of ID
        pageSize: 1000, // Get all teams for this age category
        tournamentSeason: tournamentSeasonId || undefined, // Pass tournamentSeasonId if available
        sport: sport // FIX: Pass sport parameter
      });
      
      const teamsData = response.data?.paginatedResults || response.paginatedResults || [];
      
      let filteredTeamsData = teamsData;
      
      // If group is selected, filter teams by group using standings API
      if (groupName) {
        try {
          const standingsResponse = await getTeamStandings(ageCategoryId, sport, tournamentSeasonId);
          if (Array.isArray(standingsResponse) && standingsResponse.length > 0) {
            // Check if it's group standings (has groupName property)
            if (standingsResponse[0].hasOwnProperty('groupName')) {
              const groupStanding = standingsResponse.find((group: any) => group.groupName === groupName) as any;
              if (groupStanding && groupStanding.standings) {
                const groupTeamIds = groupStanding.standings.map((standing: any) => standing.team._id);
                // NEW API STRUCTURE: _id is teamSeason id, team._id is the actual team id
                filteredTeamsData = teamsData.filter((team: any) => {
                  const teamData = team.team || team; // Use nested team object if available
                  return groupTeamIds.includes(teamData._id) || groupTeamIds.includes(team._id);
                });
              }
            }
          }
        } catch (error) {
          console.error('Error filtering teams by group:', error);
        }
      }
      
      // NEW API STRUCTURE: _id is teamSeason id, team._id is the actual team id
      const options = filteredTeamsData.map((team: any) => {
        const teamData = team.team || team; // Use nested team object if available, fallback to team itself
        return {
          value: teamData._id || team._id, // Use team._id (actual team ID) first, fallback to _id for backward compatibility
          label: teamData.name || team.name || ''
        };
      });
      
      
      setFilteredTeams(options);
    } catch (error) {
      console.error('Error loading teams by age category:', error);
      setFilteredTeams([]);
    } finally {
      setTeamsLoading(false);
    }
  }, [tournamentId, ageCategories, tournamentSeasonId]);

  // Load players based on selected age category
  const loadPlayersByAgeCategory = useCallback(async (ageCategoryId: string) => {
    if (!ageCategoryId || !tournamentId) {
      setFilteredPlayers([]);
      return;
    }
    
    // Find the age category object to get the label (name)
    const ageCategoryObj = ageCategories.find(ac => ac.value === ageCategoryId);
    if (!ageCategoryObj) {
      console.error('Age category object not found for ID:', ageCategoryId);
      setFilteredPlayers([]);
      return;
    }
    
    const ageCategoryName = ageCategoryObj.label;
    
    setPlayersLoading(true);
    try {
      const sport = getSport();
      const response = await listPlayers({
        tournament: tournamentId,
        ageCategory: ageCategoryName,
        pageSize: 1000, // Get all players for this age category
        tournamentSeason: tournamentSeasonId || undefined,
        sport: sport
      });
      
      const playersData = response.data?.paginatedResults || response.paginatedResults || [];
      
      // Map players to options format
      const options = playersData.map((player: any) => {
        const playerInfo = player.playerInfo || player;
        const playerName = playerInfo.name || player.name || 'Unknown Player';
        const playerId = playerInfo._id || player._id;
        return {
          value: playerId,
          label: playerName
        };
      });
      
      setFilteredPlayers(options);
      return options;
    } catch (error) {
      console.error('Error loading players by age category:', error);
      setFilteredPlayers([]);
      return [];
    } finally {
      setPlayersLoading(false);
    }
  }, [tournamentId, ageCategories, tournamentSeasonId, tournamentSports]);

  // Load teams/players and groups when age category changes
  useEffect(() => {
    if (form.ageCategory?.value) {
      const ageCategoryObj = ageCategories.find(ac => ac.value === form.ageCategory.value);
      
      if (ageCategoryObj) {
        // Load groups only if standings type is 'group' AND stage is not 'knockout'
        const isKnockout = form.stage?.value === 'knockout';
        if (ageCategoryObj.standingsType === 'group' && !isKnockout) {
          loadGroupsByAgeCategory(ageCategoryObj.value, ageCategoryObj.standingsType);
        } else {
          setAvailableGroups([]);
        }
        
        // Load teams or players based on format (only for Tennis/Badminton)
        // Call functions directly instead of including them in dependencies
        const shouldLoadPlayers = usesPlayerParticipants();
        if (shouldLoadPlayers) {
          // Load players for player format
          loadPlayersByAgeCategory(form.ageCategory.value);
          setFilteredTeams([]); // Clear teams
        } else {
          // Load teams for team format or other sports
          // If stage is knockout, don't filter by group (pass undefined)
          const isKnockout = form.stage?.value === 'knockout';
          const groupValue = isKnockout ? undefined : form.group?.value;
          loadTeamsByAgeCategory(form.ageCategory.value, groupValue);
          setFilteredPlayers([]); // Clear players
        }
      }
    } else {
      setFilteredTeams([]);
      setFilteredPlayers([]);
      setAvailableGroups([]);
    }
  }, [form.ageCategory?.value, form.group?.value, form.stage?.value, tournamentId, tournamentSeasonId, loadTeamsByAgeCategory, loadPlayersByAgeCategory, loadGroupsByAgeCategory, ageCategories]);

  // Load teams when group changes (only for team format)
  useEffect(() => {
    if (form.ageCategory?.value) {
      // Call functions directly instead of including them in dependencies
      const shouldLoadPlayers = usesPlayerParticipants();
      if (!shouldLoadPlayers) {
        // Only load teams if not player format
        // If stage is knockout, don't filter by group (pass undefined)
        const isKnockout = form.stage?.value === 'knockout';
        if (form.group?.value && !isKnockout) {
          loadTeamsByAgeCategory(form.ageCategory.value, form.group.value);
        } else {
          loadTeamsByAgeCategory(form.ageCategory.value);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.group?.value, form.ageCategory?.value, form.stage?.value, loadTeamsByAgeCategory]);

  // Handle pending edit data when teams are loaded
  useEffect(() => {
    if (pendingEditData) {
      // Helper function to extract time from ISO datetime string and convert to 12-hour format
      const extractTimeFromISO = (isoString: string) => {
        if (!isoString) return '';
        try {
          const date = new Date(isoString);
          // Get 24-hour format first
          const hours24 = date.getUTCHours();
          const minutes = date.getUTCMinutes().toString().padStart(2, '0');
          // Convert to 12-hour format
          return formatTimeTo12Hour(`${hours24.toString().padStart(2, '0')}:${minutes}`);
        } catch (error) {
          console.error('Error parsing ISO datetime:', error);
          return '';
        }
      };
      
      // Only process if we have teams loaded (or if teams aren't needed)
      const hasTeams = filteredTeams.length > 0 || !pendingEditData.teamA?._id;
      
      // Use loaded groups from pendingEditData if available, otherwise fall back to availableGroups state
      const loadedGroups = (pendingEditData as any)._loadedGroups || availableGroups;
      
      // Only process if we have groups loaded (or if groups aren't needed)
      const ageCategoryObj = ageCategories.find(ac => ac.value === (typeof pendingEditData.ageCategory === 'object' ? pendingEditData.ageCategory?._id : pendingEditData.ageCategory));
      const needsGroups = ageCategoryObj?.standingsType === 'group' && pendingEditData.group;
      const hasGroups = !needsGroups || loadedGroups.length > 0;
      
      if (hasTeams && hasGroups) {
        // Load knockout data if available
        const knockoutData = (pendingEditData as any)?.knockout;
        const knockoutStageOptions = [
          { value: TOURNAMENT_KNOCKOUT_STAGES.ELIMINATION, label: 'Elimination' },
          { value: TOURNAMENT_KNOCKOUT_STAGES.QUALIFIER, label: 'Qualifier' },
          { value: TOURNAMENT_KNOCKOUT_STAGES.QUARTERFINAL, label: 'Quarterfinal' },
          { value: TOURNAMENT_KNOCKOUT_STAGES.SEMIFINAL, label: 'Semifinal' },
          { value: TOURNAMENT_KNOCKOUT_STAGES.FINAL, label: 'Final' },
          { value: TOURNAMENT_KNOCKOUT_STAGES.THIRDPLACE, label: 'Third Place' },
        ];
        const knockoutStageField = knockoutData?.stage 
          ? knockoutStageOptions.find(opt => opt.value === knockoutData.stage) || null
          : null;

        const formData = {
          matchNum: pendingEditData.matchNum || '',
          time: extractTimeFromISO(pendingEditData.startTime) || (pendingEditData.time ? formatTimeTo12Hour(pendingEditData.time) : ''),
          sports: stageOptions.find(opt => opt.value === pendingEditData.sports) || { value: getSportValue().toLowerCase(), label: getSportValue() },
          stage: stageOptions.find(opt => opt.value === pendingEditData.stage) || null,
          ageCategory: ageCategoryObj || null,
          eventCategory: PICKLEBALL_EVENT_CATEGORIES.find((option) => option.value === pendingEditData.eventCategory) || null,
          group: pendingEditData.group ? (loadedGroups.find((group: any) => group.value === pendingEditData.group) || { value: pendingEditData.group, label: pendingEditData.group }) : null,
          participants: hydrateParticipants(pendingEditData, isFootball())
            .slice(0, 2)
            .map((participant) => usesMultiPlayerSides(pendingEditData.eventCategory)
              ? toParticipantOptions(participant, filteredPlayers)
              : toParticipantOption(
                  participant,
                  usesPlayerParticipants() ? filteredPlayers : filteredTeams
                )),
          date: pendingEditData.date || '',
          venue: pendingEditData.venue || '',
          venueLong: pendingEditData.venueLong || '',
          title: pendingEditData.title || '',
          subtitle: pendingEditData.subtitle || '',
          sets: (pendingEditData as any)?.setCount ? String((pendingEditData as any).setCount) : '',
          round: pendingEditData.round || (pendingEditData as any)?.round || '',
          knockoutStage: knockoutStageField,
          knockoutIndex: knockoutData?.index ? String(knockoutData.index) : '',
          knockoutMatchNum: knockoutData?.matchNum || '',
        };
        
        setForm(formData);
        setOriginalForm(JSON.parse(JSON.stringify(formData)));
        setIsFormModified(false);
        setFormTouched({});
        setFormError(null);
        setShowAddForm(true);
        setTimeout(scrollToCard, 100);
        setPendingEditData(null); // Clear pending data
      }
    }
  }, [filteredTeams, availableGroups, pendingEditData, ageCategories]);

  const getParticipantOptions = (index: number) => {
    const options = usesPlayerParticipants() ? filteredPlayers : filteredTeams;
    if (usesMultiPlayerSides()) {
      const selectedValues = (form.participants || [])
        .flatMap((participant: SelectOption[] | SelectOption | null, participantIndex: number) =>
          participantIndex === index
            ? []
            : Array.isArray(participant)
              ? participant.map((option) => option.value)
              : participant?.value ? [participant.value] : []
        );
      return options.filter((option) => !selectedValues.includes(option.value));
    }

    const selectedValue = form.participants?.[1 - index]?.value;
    return options.filter((option) => option.value !== selectedValue);
  };

  const handleParticipantChange = (index: number, option: SelectOption | SelectOption[] | null) => {
    const participants = [...(form.participants || [null, null])];
    participants[index] = option;
    setForm({ ...form, participants });
    setFormTouched({ ...formTouched, [`participant${index + 1}`]: true });
  };

  const validateForm = () => {
    const ageCategoryObj = ageCategories.find(ac => ac.value === form.ageCategory?.value);
    const isGroupStandings = ageCategoryObj?.standingsType === 'group';
    const shouldUsePlayers = usesPlayerParticipants();
    
    // Check if sport is Badminton or Table Tennis
    const sportLower = getSportValue().toLowerCase();
    const isBadmintonOrTT = sportLower === 'badminton' || sportLower === 'table tennis' || sportLower === 'tabletennis' || sportLower === 'tt';
    const requiresSetCount = isBadmintonOrTT || (isPickleball() && isPickleballDoubles(form.eventCategory?.value));
    
    // Check if stage is knockout
    const isKnockout = form.stage?.value === 'knockout';
    
    // Group is required only for league stage with group standings, not for knockout
    const groupRequired = !isKnockout && isGroupStandings && !shouldUsePlayers;
    
    // Validate sets field for Badminton/TT
    let setsError = '';
    if (requiresSetCount) {
      if (!form.sets || form.sets.trim() === '') {
        setsError = 'Sets is required for Badminton and Table Tennis.';
      } else {
        // Check if numeric
        const setsNum = parseInt(form.sets, 10);
        if (isNaN(setsNum) || setsNum <= 0) {
          setsError = 'Sets must be a positive number.';
        } else if (setsNum % 2 === 0) {
          setsError = 'Sets must be an odd number (1, 3, 5, 7, etc.).';
        }
      }
    }
    
    // Validate round field for Badminton/TT - required for knockout stage
    let roundError = '';
    if (isBadmintonOrTT && isKnockout) {
      if (!form.round || form.round.trim() === '') {
        roundError = 'Round is required for Knockout stage.';
      }
    }
    
    return {
      matchNum: !form.matchNum ? 'Match Number is required.' : '',
      time: !form.time ? 'Start Time is required.' : '',
      sports: !form.sports ? 'Sports is required.' : '',
      stage: '',
      ageCategory: !form.ageCategory ? 'Age Category is required.' : '',
      eventCategory: isPickleball() && !form.eventCategory ? 'Event Category is required.' : '',
      group: groupRequired && !form.group ? 'Group is required for group standings.' : '',
      participant1: '',
      participant2: '',
      date: !form.date ? 'Date is required.' : '',
      venue: !form.venue ? 'Venue is required.' : '',
      venueLong: !form.venueLong ? 'Venue Full Address is required.' : '',
      sets: setsError,
      round: roundError,
    };
  };
  const errors = validateForm();
  const isFormValid = Object.values(errors).every(x => !x);

  // Check if form has been modified
  const checkFormModified = () => {
    if (!originalForm) return false;
    return (
      form.matchNum !== originalForm.matchNum ||
      form.time !== originalForm.time ||
      form.sports !== originalForm.sports ||
      form.stage !== originalForm.stage ||
      form.ageCategory !== originalForm.ageCategory ||
      JSON.stringify(form.group) !== JSON.stringify(originalForm.group) ||
      JSON.stringify(form.participants) !== JSON.stringify(originalForm.participants) ||
      form.date !== originalForm.date ||
      form.venue !== originalForm.venue ||
      form.venueLong !== originalForm.venueLong ||
      form.title !== originalForm.title ||
      form.subtitle !== originalForm.subtitle
    );
  };

  React.useEffect(() => {
    setIsFormModified(checkFormModified());
  }, [form, originalForm]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  // Prevent scroll from changing numeric input values
  const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    e.currentTarget.blur();
  };

  const handleSelectChange = (name: string, option: any) => {
    if (name === 'ageCategory') {
      setForm({ 
        ...form, 
        [name]: option, 
        group: null,
        participants: [null, null],
      });
      // Mark group field as touched to trigger validation
      setFormTouched({ ...formTouched, [name]: true, group: true });
    } else if (name === 'eventCategory') {
      setForm({
        ...form,
        eventCategory: option,
        participants: [null, null],
        sets: '',
      });
      setFormTouched({ ...formTouched, eventCategory: true, participant1: true, participant2: true });
    } else if (name === 'stage') {
      // When stage changes to knockout, clear group and reload teams without group filter
      const isKnockout = option?.value === 'knockout';
      setForm({ 
        ...form, 
        [name]: option,
        group: isKnockout ? null : form.group, // Clear group if switching to knockout
        participants: isKnockout ? [null, null] : form.participants
      });
      setFormTouched({ ...formTouched, [name]: true, group: true });
    } else {
      setForm({ ...form, [name]: option });
      setFormTouched({ ...formTouched, [name]: true });
    }
  };

  const handleGroupChange = (option: any) => {
    setForm({ ...form, group: option, participants: [null, null] });
    setFormTouched({ ...formTouched, group: true });
  };

  const handleAdd = () => {
    setEditIndex(null);
    setViewMode(false);
    const sportValue = getSportValue();
    const initialForm = {
      matchNum: '',
      sports: { value: sportValue.toLowerCase(), label: sportValue }, // Use tournamentSports prop instead of hardcoded 'Football'
      stage: null,
      ageCategory: null,
      eventCategory: null,
      group: null,
      participants: [null, null],
      date: '',
      time: '',
      venue: '',
      venueLong: '',
      streamingVideoUrl: '',
    };
    setForm(initialForm);
    setOriginalForm(initialForm);
    setIsFormModified(false);
    setFormTouched({});
    setFormError(null);
    setShowAddForm(true);
    setTimeout(scrollToCard, 100);
  };

  const handleEdit = async (idx: number) => {
    const f = fixtures[idx];
    
    // If navigateToDetailPage is true, navigate to detail page instead of opening modal
    if (navigateToDetailPage && tournamentId) {
      // Determine if we're coming from tournament detail page (not season detail page)
      // If current pathname doesn't include '/seasons/', we're on tournament detail page
      const currentPath = window.location.pathname;
      const isFromTournamentDetailPage = !currentPath.includes('/seasons/');
      
      // Create selectedTournament object with sports property for MatchDetailPage
      const selectedTournament = tournamentSports ? { sports: tournamentSports } : null;
      
      navigate(`/manage-tournaments-fixtures/${tournamentId}/match/${f._id}${fixtureContextQuery()}`, {
        state: {
          ...(tournamentSeasonId ? { tournamentSeasonId } : {}),
          fromTournamentDetailPage: isFromTournamentDetailPage,
          ...(selectedTournament ? { selectedTournament } : {})
        }
      });
      return;
    }
    
    setEditIndex(idx);
    setViewMode(false);
    
    try {
      // Fetch detailed fixture data
      const detailedFixture = await getFixtureById(f._id);
      // Handle different response structures
      const fixtureData = detailedFixture.data || detailedFixture;
      
      if (!fixtureData) {
        throw new Error('No fixture data received from API');
      }
      
      // Load groups and teams for the age category before processing
      const ageCategoryValue = typeof fixtureData.ageCategory === 'object' ? fixtureData.ageCategory?._id : fixtureData.ageCategory;
      let loadedGroups: { value: string; label: string }[] = [];
      
      if (ageCategoryValue) {
        // Find age category object to get standingsType
        const ageCategoryObj = ageCategories.find(ac => ac.value === ageCategoryValue);
        if (ageCategoryObj && ageCategoryObj.standingsType === 'group') {
          // Load groups first if it's a group standings type
          loadedGroups = await loadGroupsByAgeCategory(ageCategoryValue, ageCategoryObj.standingsType);
        }
        // Load teams (with group if available)
        await loadTeamsByAgeCategory(ageCategoryValue, fixtureData.group);
      }
      
      // Store the fixture data with loaded groups to be processed when teams are loaded
      setPendingEditData({ ...fixtureData, _loadedGroups: loadedGroups });
    } catch (error) {
      console.error('Error fetching fixture details:', error);
      
      // Even if API fails, open form with basic data from the list
      // Helper function to extract time from ISO datetime string
      const extractTimeFromISO = (isoString: string) => {
        if (!isoString) return '';
        try {
          const date = new Date(isoString);
          const hours = date.getUTCHours().toString().padStart(2, '0');
          const minutes = date.getUTCMinutes().toString().padStart(2, '0');
          return `${hours}:${minutes}`;
        } catch (error) {
          console.error('Error parsing ISO datetime:', error);
          return '';
        }
      };
      
      // Load knockout data if available
      const knockoutData = (f as any)?.knockout;
      const knockoutStageOptions = [
        { value: TOURNAMENT_KNOCKOUT_STAGES.ELIMINATION, label: 'Elimination' },
        { value: TOURNAMENT_KNOCKOUT_STAGES.QUALIFIER, label: 'Qualifier' },
        { value: TOURNAMENT_KNOCKOUT_STAGES.QUARTERFINAL, label: 'Quarterfinal' },
        { value: TOURNAMENT_KNOCKOUT_STAGES.SEMIFINAL, label: 'Semifinal' },
        { value: TOURNAMENT_KNOCKOUT_STAGES.FINAL, label: 'Final' },
        { value: TOURNAMENT_KNOCKOUT_STAGES.THIRDPLACE, label: 'Third Place' },
      ];
      const knockoutStageField = knockoutData?.stage 
        ? knockoutStageOptions.find(opt => opt.value === knockoutData.stage) || null
        : null;

      const formData = {
        matchNum: f.matchNum || '',
        time: extractTimeFromISO(f.startTime || '') || f.time || '',
        sports: stageOptions.find(opt => opt.value === f.sports) || { value: getSportValue().toLowerCase(), label: getSportValue() },
        stage: stageOptions.find(opt => opt.value === f.stage) || null,
        ageCategory: ageCategories.find(opt => opt.value === (typeof f.ageCategory === 'object' ? f.ageCategory?._id : f.ageCategory)) || null,
        eventCategory: PICKLEBALL_EVENT_CATEGORIES.find((option) => option.value === f.eventCategory) || null,
        group: f.group || '',
        participants: hydrateParticipants(f, isFootball())
          .slice(0, 2)
          .map((participant) => usesMultiPlayerSides(f.eventCategory)
            ? toParticipantOptions(participant, filteredPlayers)
            : toParticipantOption(
                participant,
                usesPlayerParticipants() ? filteredPlayers : filteredTeams
              )),
        date: f.date || '',
        venue: f.venue || '',
        venueLong: f.venueLong || '',
        title: f.title || '',
        subtitle: f.subtitle || '',
        streamingVideoUrl: (f as any)?.streamingVideoUrl || '',
        knockoutStage: knockoutStageField,
        knockoutIndex: knockoutData?.index ? String(knockoutData.index) : '',
        knockoutMatchNum: knockoutData?.matchNum || '',
      };
      
      
      setForm(formData);
      setOriginalForm(JSON.parse(JSON.stringify(formData)));
      setIsFormModified(false);
      setFormTouched({});
      setFormError(null);
      setShowAddForm(true);
      setTimeout(scrollToCard, 100);
    }
  };

  // Opens the match detail page's Gallery section directly — that section
  // is always rendered there, so this only makes sense in the
  // navigateToDetailPage mode (the standalone edit-modal mode has no
  // detail page to link into).
  const handleOpenGallery = (idx: number) => {
    const f = fixtures[idx];
    if (!(navigateToDetailPage && tournamentId)) return;
    const selectedTournament = tournamentSports ? { sports: tournamentSports } : null;
    navigate(`/manage-tournaments-fixtures/${tournamentId}/match/${f._id}${fixtureContextQuery()}#gallery`, {
      state: {
        ...(tournamentSeasonId ? { tournamentSeasonId } : {}),
        ...(selectedTournament ? { selectedTournament } : {}),
      },
    });
  };

  const handleView = async (idx: number) => {
    const f = fixtures[idx];
    
    // If navigateToDetailPage is true, navigate to detail page instead of opening modal
    if (navigateToDetailPage && tournamentId) {
      // Determine if we're coming from tournament detail page (not season detail page)
      // If current pathname doesn't include '/seasons/', we're on tournament detail page
      const currentPath = window.location.pathname;
      const isFromTournamentDetailPage = !currentPath.includes('/seasons/');
      
      // Create selectedTournament object with sports property for MatchDetailPage
      const selectedTournament = tournamentSports ? { sports: tournamentSports } : null;
      
      navigate(`/manage-tournaments-fixtures/${tournamentId}/match/${f._id}${fixtureContextQuery()}`, {
        state: {
          ...(tournamentSeasonId ? { tournamentSeasonId } : {}),
          fromTournamentDetailPage: isFromTournamentDetailPage,
          ...(selectedTournament ? { selectedTournament } : {})
        }
      });
      return;
    }
    
    setEditIndex(idx);
    setViewMode(true);
    
    try {
      // Fetch detailed fixture data
      const detailedFixture = await getFixtureById(f._id);
      console.log('Full API response:', detailedFixture);
      
      const fixtureData = detailedFixture.data || detailedFixture;
      
      // Helper function to extract time from ISO datetime string and convert to 12-hour format
      const extractTimeFromISO = (isoString: string) => {
        if (!isoString) return '';
        try {
          const date = new Date(isoString);
          // Get 24-hour format first
          const hours24 = date.getUTCHours();
          const minutes = date.getUTCMinutes().toString().padStart(2, '0');
          // Convert to 12-hour format
          return formatTimeTo12Hour(`${hours24.toString().padStart(2, '0')}:${minutes}`);
        } catch (error) {
          console.error('Error parsing ISO datetime:', error);
          return '';
        }
      };
      
      // Load groups and teams for the age category before setting form
      const ageCategoryValue = typeof fixtureData.ageCategory === 'object' ? fixtureData.ageCategory?._id : fixtureData.ageCategory;
      let loadedGroups: { value: string; label: string }[] = [];
      
      if (ageCategoryValue) {
        // Find age category object to get standingsType
        const ageCategoryObj = ageCategories.find(ac => ac.value === ageCategoryValue);
        if (ageCategoryObj && ageCategoryObj.standingsType === 'group') {
          // Load groups first if it's a group standings type
          loadedGroups = await loadGroupsByAgeCategory(ageCategoryValue, ageCategoryObj.standingsType);
        }
        // Load teams (with group if available)
        await loadTeamsByAgeCategory(ageCategoryValue, fixtureData.group);
      }
      
      // Load knockout data if available
      const knockoutData = (fixtureData as any)?.knockout;
      const knockoutStageOptions = [
        { value: TOURNAMENT_KNOCKOUT_STAGES.ELIMINATION, label: 'Elimination' },
        { value: TOURNAMENT_KNOCKOUT_STAGES.QUALIFIER, label: 'Qualifier' },
        { value: TOURNAMENT_KNOCKOUT_STAGES.QUARTERFINAL, label: 'Quarterfinal' },
        { value: TOURNAMENT_KNOCKOUT_STAGES.SEMIFINAL, label: 'Semifinal' },
        { value: TOURNAMENT_KNOCKOUT_STAGES.FINAL, label: 'Final' },
        { value: TOURNAMENT_KNOCKOUT_STAGES.THIRDPLACE, label: 'Third Place' },
      ];
      const knockoutStageField = knockoutData?.stage 
        ? knockoutStageOptions.find(opt => opt.value === knockoutData.stage) || null
        : null;

      const formData = {
        matchNum: fixtureData.matchNum || '',
        sports: { value: fixtureData.sports || getSport(), label: fixtureData.sports || getSport() },
        stage: stageOptions.find(opt => opt.value === fixtureData.stage) || null,
        ageCategory: ageCategories.find(opt => opt.value === (typeof fixtureData.ageCategory === 'object' ? fixtureData.ageCategory?._id : fixtureData.ageCategory)) || null,
        eventCategory: PICKLEBALL_EVENT_CATEGORIES.find((option) => option.value === fixtureData.eventCategory) || null,
        group: fixtureData.group ? (loadedGroups.find(group => group.value === fixtureData.group) || availableGroups.find(group => group.value === fixtureData.group) || { value: fixtureData.group, label: fixtureData.group }) : null,
        participants: hydrateParticipants(fixtureData, isFootball())
          .slice(0, 2)
          .map((participant) => usesMultiPlayerSides(fixtureData.eventCategory)
            ? toParticipantOptions(participant, filteredPlayers)
            : toParticipantOption(
                participant,
                usesPlayerParticipants() ? filteredPlayers : filteredTeams
              )),
        date: fixtureData.date || '',
        time: extractTimeFromISO(fixtureData.startTime) || (fixtureData.time ? formatTimeTo12Hour(fixtureData.time) : ''),
        venue: fixtureData.venue || '',
        venueLong: fixtureData.venueLong || '',
        title: fixtureData.title || '',
        subtitle: fixtureData.subtitle || '',
        streamingVideoUrl: (fixtureData as any)?.streamingVideoUrl || '',
        sets: (fixtureData as any)?.setCount ? String((fixtureData as any).setCount) : '',
        round: fixtureData.round || (fixtureData as any)?.round || '',
        knockoutStage: knockoutStageField,
        knockoutIndex: knockoutData?.index ? String(knockoutData.index) : '',
        knockoutMatchNum: knockoutData?.matchNum || '',
      };
      
      setForm(formData);
      setOriginalForm(JSON.parse(JSON.stringify(formData)));
      setIsFormModified(false);
      setFormTouched({});
      setFormError(null);
      setShowAddForm(true);
      setTimeout(scrollToCard, 100);
    } catch (error) {
      console.error('Error fetching fixture details:', error);
      
      // Even if API fails, open form with basic data from the list
      // Helper function to extract time from ISO datetime string and convert to 12-hour format
      const extractTimeFromISO = (isoString: string) => {
        if (!isoString) return '';
        try {
          const date = new Date(isoString);
          // Get 24-hour format first
          const hours24 = date.getUTCHours();
          const minutes = date.getUTCMinutes().toString().padStart(2, '0');
          // Convert to 12-hour format
          return formatTimeTo12Hour(`${hours24.toString().padStart(2, '0')}:${minutes}`);
        } catch (error) {
          console.error('Error parsing ISO datetime:', error);
          return '';
        }
      };
      
      // Load groups and teams for the age category if available
      const ageCategoryValue = typeof f.ageCategory === 'object' ? f.ageCategory?._id : f.ageCategory;
      if (ageCategoryValue) {
        const ageCategoryObj = ageCategories.find(ac => ac.value === ageCategoryValue);
        if (ageCategoryObj && ageCategoryObj.standingsType === 'group') {
          await loadGroupsByAgeCategory(ageCategoryValue, ageCategoryObj.standingsType);
        }
        await loadTeamsByAgeCategory(ageCategoryValue, f.group);
      }
      
      // Load knockout data if available
      const knockoutDataViewFallback = (f as any)?.knockout;
      const knockoutStageOptionsViewFallback = [
        { value: TOURNAMENT_KNOCKOUT_STAGES.ELIMINATION, label: 'Elimination' },
        { value: TOURNAMENT_KNOCKOUT_STAGES.QUALIFIER, label: 'Qualifier' },
        { value: TOURNAMENT_KNOCKOUT_STAGES.QUARTERFINAL, label: 'Quarterfinal' },
        { value: TOURNAMENT_KNOCKOUT_STAGES.SEMIFINAL, label: 'Semifinal' },
        { value: TOURNAMENT_KNOCKOUT_STAGES.FINAL, label: 'Final' },
        { value: TOURNAMENT_KNOCKOUT_STAGES.THIRDPLACE, label: 'Third Place' },
      ];
      const knockoutStageFieldViewFallback = knockoutDataViewFallback?.stage 
        ? knockoutStageOptionsViewFallback.find(opt => opt.value === knockoutDataViewFallback.stage) || null
        : null;

      const formData = {
        matchNum: f.matchNum || '',
        time: extractTimeFromISO(f.startTime || '') || (f.time ? formatTimeTo12Hour(f.time) : ''),
        sports: stageOptions.find(opt => opt.value === f.sports) || { value: getSportValue().toLowerCase(), label: getSportValue() },
        stage: stageOptions.find(opt => opt.value === f.stage) || null,
        ageCategory: ageCategories.find(opt => opt.value === (typeof f.ageCategory === 'object' ? f.ageCategory?._id : f.ageCategory)) || null,
        eventCategory: PICKLEBALL_EVENT_CATEGORIES.find((option) => option.value === f.eventCategory) || null,
        group: f.group ? (availableGroups.find(group => group.value === f.group) || { value: f.group, label: f.group }) : null,
        participants: hydrateParticipants(f, isFootball())
          .slice(0, 2)
          .map((participant) => usesMultiPlayerSides(f.eventCategory)
            ? toParticipantOptions(participant, filteredPlayers)
            : toParticipantOption(
                participant,
                usesPlayerParticipants() ? filteredPlayers : filteredTeams
              )),
        date: f.date || '',
        venue: f.venue || '',
        venueLong: f.venueLong || '',
        title: f.title || '',
        subtitle: f.subtitle || '',
        streamingVideoUrl: (f as any)?.streamingVideoUrl || '',
        sets: (f as any)?.setCount ? String((f as any).setCount) : '',
        round: f.round || (f as any)?.round || '',
        knockoutStage: knockoutStageFieldViewFallback,
        knockoutIndex: knockoutDataViewFallback?.index ? String(knockoutDataViewFallback.index) : '',
        knockoutMatchNum: knockoutDataViewFallback?.matchNum || '',
      };
      
      setForm(formData);
      setOriginalForm(JSON.parse(JSON.stringify(formData)));
      setIsFormModified(false);
      setFormTouched({});
      setFormError(null);
      setShowAddForm(true);
      setTimeout(scrollToCard, 100);
    }
  };

  const handleDelete = (idx: number) => {
    setDeleteIndex(idx);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (deleteIndex !== null) {
      const fixture = fixtures[deleteIndex];
      try {
        // Always pass sport to delete API to satisfy backend requirement
        let sportForDelete: string;
        try {
          sportForDelete = getSport();
        } catch {
          // Fallback: try to derive sport from fixture data, then default to Football
          const fixtureSport = (fixture as any)?.sports || (fixture as any)?.sport;
          sportForDelete = (fixtureSport as string) || 'Football';
        }
        
        await deleteFixture(fixture._id, sportForDelete);
        onDelete();
        setShowDeleteModal(false);
        setDeleteIndex(null);
      } catch (error: any) {
        console.error('Error deleting fixture:', error);
        console.error('Error response:', error.response);
        console.error('Error response data:', error.response?.data);
        
        // Extract detailed error message from API response
        let errorMessage = 'Failed to delete fixture. Please try again.';
        
        if (error.response) {
          // API responded with error status
          const responseData = error.response.data;
          
          if (responseData?.message) {
            // Use the specific error message from API
            errorMessage = responseData.message;
          } else if (responseData?.error) {
            // Alternative error field
            errorMessage = responseData.error;
          } else if (typeof responseData === 'string') {
            // Response data is a string
            errorMessage = responseData;
          } else if (responseData?.errors && Array.isArray(responseData.errors)) {
            // Multiple validation errors
            errorMessage = responseData.errors.join(', ');
          } else if (responseData?.details) {
            // Error details field
            errorMessage = responseData.details;
          }
        } else if (error.message) {
          // Network or other error
          errorMessage = error.message;
        }
        
        setFormError(errorMessage);
      }
    }
  };

  const scrollToCard = () => {
    if (cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleClose = () => setShowAddForm(false);

  // Bulk upload handlers
  const handleBulkUploadClick = () => {
    setShowBulkUploadModal(true);
    setUploadFile(null);
    setUploadProgress(0);
    setUploadError(null);
    setUploadSuccess(false);
    setUploadResponseData(null);
    setShowResponseData(false);
  };

  const handleBulkUploadClose = () => {
    setShowBulkUploadModal(false);
    setUploadFile(null);
    setUploadProgress(0);
    setUploadError(null);
    setUploadSuccess(false);
    setUploadResponseData(null);
    setShowResponseData(false);
  };

  const handleReupload = () => {
    // Reset for new upload
    setUploadFile(null);
    setUploadProgress(0);
    setUploadError(null);
    setUploadSuccess(false);
    setUploadResponseData(null);
    setShowResponseData(false);
  };

  const handleAcknowledgeResponse = () => {
    // User has reviewed the response, close modal and refresh fixtures
    handleBulkUploadClose();
    onAdd(); // Refresh the fixtures list
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
        'application/vnd.ms-excel' // .xls
      ];
      
      if (!validTypes.includes(file.type)) {
        setUploadError('Please select a valid Excel file (.xlsx or .xls)');
        return;
      }
      
      setUploadFile(file);
      setUploadError(null);
    }
  };

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      // Validate file type
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
        'application/vnd.ms-excel' // .xls
      ];
      
      if (!validTypes.includes(file.type)) {
        setUploadError('Please select a valid Excel file (.xlsx or .xls)');
        return;
      }
      
      setUploadFile(file);
      setUploadError(null);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleBulkUpload = async () => {
    if (!uploadFile || !tournamentId) return;

    setUploading(true);
    setUploadError(null);
    setUploadProgress(0);

    try {
      // Step 1: Get presigned URL
      setUploadProgress(25);
      const presignedUrlRequest: PresignedUrlRequest = {
        fileName: uploadFile.name,
        contentType: uploadFile.type,
        context: 'spreadsheets'
      };

      const presignedResponse: PresignedUrlResponse = await getPresignedUrl(presignedUrlRequest);
      setUploadProgress(50);

      // Extract fileKey from response (API might return 'key' instead of 'fileKey')
      const actualFileKey = presignedResponse.fileKey || presignedResponse.key;
      
      if (!actualFileKey) {
        throw new Error('No fileKey received from presigned URL response');
      }

      // Step 2: Upload file to S3
      const uploadResponse = await fetch(presignedResponse.presignedUrl, {
        method: 'PUT',
        body: uploadFile,
        headers: {
          'Content-Type': uploadFile.type,
        },
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        console.error('S3 upload failed:', { status: uploadResponse.status, errorText });
        throw new Error(`Failed to upload file to S3: ${uploadResponse.status} - ${errorText}`);
      }
      setUploadProgress(75);

      // Step 3: Process bulk upload
      const bulkUploadRequest: BulkUploadRequest = {
        fileKey: actualFileKey
      };
      
      // Pass tournamentSeasonId and sport to bulk upload API
      const sport = getSportValue();
      const response = await bulkUploadFixtures(tournamentId, bulkUploadRequest, tournamentSeasonId || undefined, sport);
      setUploadProgress(100);

      // Handle different response structures
      // New structure (Badminton/TT): { statusCode: 200, data: { errors: [...], fixtures: [] } }
      // Old structure (Football): { statusCode: 200, data: ["error1", "error2"] } OR ["error1", "error2"]
      let hasErrors = false;
      let errorData = null;

      // Case 1: New API structure - Response has data.errors array (Badminton/TT)
      // Structure: { statusCode: 200, data: { errors: [...], fixtures: [] } }
      if (response && response.data && response.data.errors && Array.isArray(response.data.errors) && response.data.errors.length > 0) {
        hasErrors = true;
        errorData = response;
      }
      // Case 2: Legacy API - Response is directly an array (Football - old format)
      // Structure: ["error1", "error2"]
      else if (Array.isArray(response) && response.length > 0) {
        hasErrors = true;
        errorData = {
          statusCode: 200,
          status: 'success',
          message: 'Fixture uploaded for Tournament successfully',
          data: response
        };
      }
      // Case 3: Legacy API - Response has data property with array (Football - old format)
      // Structure: { statusCode: 200, data: ["error1", "error2"] }
      else if (response && response.data && Array.isArray(response.data) && response.data.length > 0 && !response.data.errors) {
        hasErrors = true;
        errorData = response;
      }
      // Case 4: No errors found - successful upload
      else {
        hasErrors = false;
      }

      if (hasErrors) {
        // Show response data to user as errors/warnings
        setUploadResponseData(errorData);
        setShowResponseData(true);
        setUploadSuccess(false);
        // Don't close modal or refresh fixtures - let user review the response
      } else {
        // No errors - successful upload
        setUploadSuccess(true);
        setTimeout(() => {
          handleBulkUploadClose();
          onAdd(); // Refresh the fixtures list
        }, 2000);
      }

    } catch (error: any) {
      console.error('Bulk upload error:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      
      // Extract detailed error message from API response
      let errorMessage = 'Upload failed. Please try again.';
      
      if (error.response) {
        // API responded with error status
        const responseData = error.response.data;
        
        if (responseData) {
          // Try different possible error message fields
          errorMessage = responseData.message || 
                        responseData.error || 
                        responseData.details || 
                        responseData.description ||
                        responseData.errorMessage ||
                        responseData.msg ||
                        `Request failed with status code ${error.response.status}`;
          
          // Handle specific S3 file reading errors
          if (responseData.error && responseData.error.includes('Error reading xlsx file from S3')) {
            errorMessage = 'File processing error: Unable to read the uploaded Excel file. Please ensure the file is not corrupted and try again.';
          }
        } else {
          errorMessage = `Request failed with status code ${error.response.status}`;
        }
      } else if (error.request) {
        // Network error - no response received
        console.error('Network error - no response received:', error.request);
        errorMessage = 'Network error. Please check your connection and try again.';
      } else {
        // Other error
        console.error('Other error:', error.message);
        errorMessage = error.message || 'An unexpected error occurred.';
      }
      
      setUploadError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setFormTouched({
      matchNum: true,
      time: true,
      sports: true,
      stage: true,
      ageCategory: true,
      group: true,
      participant1: true,
      participant2: true,
      date: true,
      venue: true,
      venueLong: true,
      sets: true,
      round: true,
    });
    if (!isFormValid) return;
    setFormError(null);
    setSaving(true);
    try {
      // Convert time from 12-hour format to 24-hour format for the payload
      const time24Hour = formatTimeTo24Hour(form.time);
      
      const shouldUsePlayers = usesPlayerParticipants();
      
      const payload: any = {
        ageCategory: form.ageCategory.value, // This should be ObjectId (ageCategory._id), not the name
        sports: form.sports.label, // Use capitalized label (e.g., "Football")
        stage: form.stage?.value?.toLowerCase() || null, // Ensure lowercase (e.g., "knockout", "league")
        group: form.group?.value || '',
        matchNum: parseInt(form.matchNum),
        date: form.date,
        time: time24Hour, // Converted to 24-hour format (e.g., "20:00")
        venue: form.venue,
        venueLong: form.venueLong,
        title: form.title,
        subtitle: form.subtitle,
        streamingVideoUrl: form.streamingVideoUrl || null,
        format: shouldUsePlayers ? 'player' : 'team',
        ...(isPickleball() ? { eventCategory: form.eventCategory?.value } : {}),
        participants: form.participants.map((participant: SelectOption | SelectOption[] | null, index: number) => ({
          slot: index + 1,
          ...(shouldUsePlayers
            ? {
                players: usesMultiPlayerSides()
                  ? (Array.isArray(participant) ? participant.map((player) => player.value) : [])
                  : !Array.isArray(participant) && participant ? [participant.value] : [],
              }
            : { team: !Array.isArray(participant) ? participant?.value || null : null }),
        })),
      };
      
      // Get sport value for API calls
      const sport = getSportValue();
      const sportLower = sport.toLowerCase();
      
      // Add sets and round for Badminton and Table Tennis
      const isBadmintonOrTT = sportLower === 'badminton' || sportLower === 'table tennis' || sportLower === 'tabletennis' || sportLower === 'tt';
      const requiresSetCount = isBadmintonOrTT || (isPickleball() && isPickleballDoubles(form.eventCategory?.value));
      if (requiresSetCount) {
        if (form.sets) {
          payload.setCount = parseInt(form.sets, 10);
        }
        if (form.round) {
          payload.round = form.round;
        }
      }
      
      // Add knockout fields for Football when stage is knockout
      if (!isBadmintonOrTT && form.stage?.value === 'knockout') {
        payload.knockout = {
          stage: form.knockoutStage?.value || null,
          index: form.knockoutIndex ? parseInt(form.knockoutIndex, 10) : null,
          matchNum: form.knockoutMatchNum || null,
        };
      }
      
      // Always include tournamentId
      payload.tournament = tournamentId;
      
      // If tournamentSeasonId is provided (season context), also include it
      if (tournamentSeasonId) {
        payload.tournamentSeason = tournamentSeasonId;
      }
      
      if (editIndex !== null) {
        await editFixture(fixtures[editIndex]._id, payload, sport);
      } else {
        await addFixture(payload, sport);
      }
      
      setShowAddForm(false);
      setEditIndex(null);
      setFormError(null);
      onAdd();
    } catch (error: any) {
      console.error('Error saving fixture:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      
      // Extract detailed error message from API response
      let errorMessage = 'Failed to save fixture. Please try again.';
      
      if (error.response) {
        // API responded with error status
        const responseData = error.response.data;
        
        if (responseData?.message) {
          // Use the specific error message from API
          errorMessage = responseData.message;
        } else if (responseData?.error) {
          // Alternative error field
          errorMessage = responseData.error;
        } else if (typeof responseData === 'string') {
          // Response data is a string
          errorMessage = responseData;
        } else if (responseData?.errors && Array.isArray(responseData.errors)) {
          // Multiple validation errors
          errorMessage = responseData.errors.join(', ');
        } else if (responseData?.details) {
          // Error details field
          errorMessage = responseData.details;
        }
      } else if (error.message) {
        // Network or other error
        errorMessage = error.message;
      }
      
      setFormError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {/* Search and Filter Bar */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-3">
          <h4 className="mb-0">Fixtures</h4>
          <div className="search-box" style={{ position: 'relative' }}>
            <input
              type="text"
              className="form-control"
              placeholder="Search fixtures..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              style={{ 
                minWidth: '250px',
                paddingRight: inputValue ? '30px' : '12px',
                paddingLeft: '12px'
              }}
            />
            {inputValue && (
              <button
                type="button"
                onClick={() => setInputValue('')}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#6c757d',
                  cursor: 'pointer',
                  fontSize: '16px',
                  padding: '0',
                  width: '20px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Clear search"
              >
                ×
              </button>
            )}
          </div>
          <div className="filter-box">
            <Select
              classNamePrefix="react-select"
              value={ageCategoryFilter}
              onChange={onAgeCategoryFilterChange}
              options={ageCategories}
              placeholder="Age Category"
              isClearable
              styles={{ control: base => ({ ...base, minWidth: '150px' }) }}
            />
          </div>
          {onStatusFilterChange && (
            <div className="filter-box">
              <Select
                classNamePrefix="react-select"
                value={statusFilter || statusOptions[0]}
                onChange={(opt: any) => onStatusFilterChange(opt?.value ? opt : null)}
                options={statusOptions}
                placeholder="Match Status"
                isClearable={false}
                styles={{ control: base => ({ ...base, minWidth: '150px' }) }}
              />
            </div>
          )}
        </div>
        <div className="d-flex gap-2 align-items-center">
          <AddNewButton onClick={handleAdd} />
          <Button 
            variant="outline-primary" 
            onClick={() => setShowTournamentBulkUpload(true)}
            disabled={!tournamentSeasonId || !tournamentSports}
            title={!tournamentSeasonId ? 'Bulk uploads require a tournament season' : undefined}
            className="d-flex align-items-center gap-2"
            style={{ 
              height: '36px',
              borderRadius: '10px',
              padding: '8px 16px',
              fontSize: '13px',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <i className="ri-upload-2-line"></i>
            Bulk Upload
          </Button>
          {onDownloadExcel && (
            <Button 
              variant="success" 
              onClick={onDownloadExcel}
              disabled={isDownloadingExcel}
              className="d-flex align-items-center gap-2"
              title="Download fixtures as Excel file"
              style={{ 
                height: '36px',
                borderRadius: '10px',
                padding: '8px 16px',
                fontSize: '13px',
                fontWeight: 500,
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
            >
              <i className="ri-file-excel-line"></i>
              {isDownloadingExcel ? 'Downloading...' : 'Download Excel'}
            </Button>
          )}
        </div>
      </div>

      {/* Add/Edit Form Card */}
      {showAddForm && (
        <Card ref={cardRef} className="mb-4" style={{ border: '1px solid #e3e6f0' }}>
          <Card.Header className="d-flex justify-content-between align-items-center" style={{ backgroundColor: '#f8f9fc' }}>
            <h6 className="mb-0">
              {editIndex === null ? 'Add New Fixture' : viewMode ? 'View Fixture' : 'Edit Fixture'}
            </h6>
            <Button variant="link" onClick={handleClose} className="p-0">
              <i className="ri-close-line" style={{ fontSize: '1.5rem' }}></i>
            </Button>
          </Card.Header>
          <Card.Body>
            {formError && <div className="alert alert-danger">{formError}</div>}
            <Form>
              <div className="row">
                <div className="col-md-3">
                  <Form.Group className="mb-3">
                    <Form.Label>Match No<span style={{ color: 'red' }}>*</span></Form.Label>
                    <Form.Control 
                      name="matchNum" 
                      type="number"
                      value={form.matchNum} 
                      onChange={handleInputChange} 
                      onWheel={handleWheel}
                      isInvalid={!!errors.matchNum && formTouched.matchNum} 
                      onBlur={() => setFormTouched({ ...formTouched, matchNum: true })}
                      readOnly={viewMode} 
                    />
                    {(errors.matchNum && formTouched.matchNum) && <div className="text-danger">{errors.matchNum}</div>}
                  </Form.Group>
                </div>
                <div className="col-md-3">
                  <Form.Group className="mb-3">
                    <Form.Label>Sports<span style={{ color: 'red' }}>*</span></Form.Label>
                    <Form.Control 
                      name="sports" 
                      value={form.sports?.label || 'Football'} 
                      readOnly
                      style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-3">
                  <Form.Group className="mb-3">
                    <Form.Label>Age Category<span style={{ color: 'red' }}>*</span></Form.Label>
                    <Select
                      classNamePrefix="react-select"
                      name="ageCategory"
                      value={form.ageCategory}
                      onChange={option => handleSelectChange('ageCategory', option)}
                      options={ageCategories}
                      placeholder="Select Age Category"
                      isClearable={false}
                      isDisabled={viewMode}
                      styles={{ control: base => ({ ...base, minHeight: '38px' }) }}
                    />
                    {(errors.ageCategory && formTouched.ageCategory) && <div className="text-danger">{errors.ageCategory}</div>}
                  </Form.Group>
                </div>
                {isPickleball() && (
                  <div className="col-md-3">
                    <Form.Group className="mb-3">
                      <Form.Label>Event Category<span style={{ color: 'red' }}>*</span></Form.Label>
                      <Select
                        classNamePrefix="react-select"
                        name="eventCategory"
                        value={form.eventCategory}
                        onChange={option => handleSelectChange('eventCategory', option)}
                        options={PICKLEBALL_EVENT_CATEGORIES}
                        placeholder="Select Event Category"
                        isClearable={false}
                        isDisabled={viewMode}
                        styles={{ control: base => ({ ...base, minHeight: '38px' }) }}
                      />
                      {(errors.eventCategory && formTouched.eventCategory) && <div className="text-danger">{errors.eventCategory}</div>}
                    </Form.Group>
                  </div>
                )}
                <div className="col-md-3">
                  <Form.Group className="mb-3">
                    <Form.Label>Stage</Form.Label>
                    <Select
                      classNamePrefix="react-select"
                      name="stage"
                      value={form.stage}
                      onChange={option => handleSelectChange('stage', option)}
                      options={stageOptions}
                      placeholder="Select Stage"
                      isClearable
                      isDisabled={viewMode}
                      styles={{ control: base => ({ ...base, minHeight: '38px' }) }}
                    />
                    {(errors.stage && formTouched.stage) && <div className="text-danger">{errors.stage}</div>}
                  </Form.Group>
                </div>
              </div>
              {/* Sets and Round fields - for Badminton/Table Tennis and Pickleball doubles */}
              {(() => {
                const sportLower = getSportValue().toLowerCase();
                const isBadmintonOrTT = sportLower === 'badminton' || sportLower === 'table tennis' || sportLower === 'tabletennis' || sportLower === 'tt';
                const requiresSetCount = isBadmintonOrTT || (isPickleball() && isPickleballDoubles(form.eventCategory?.value));
                
                if (!requiresSetCount) {
                  return null;
                }
                
                const isKnockout = form.stage?.value === 'knockout';
                
                return (
                  <div className="row">
                    <div className="col-md-6">
                      <Form.Group className="mb-3">
                        <Form.Label>Sets<span style={{ color: 'red' }}>*</span></Form.Label>
                        <Form.Control 
                          name="sets" 
                          type="number"
                          min="1"
                          step="2"
                          value={form.sets} 
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const value = e.target.value;
                            // Only allow numeric input
                            if (value === '' || /^\d+$/.test(value)) {
                              setForm({ ...form, sets: value });
                            }
                          }}
                          onBlur={() => {
                            setFormTouched({ ...formTouched, sets: true });
                          }}
                          isInvalid={!!errors.sets && formTouched.sets} 
                          readOnly={viewMode}
                          placeholder="Enter sets (1, 3, 5, 7...)" 
                        />
                        <Form.Text className="text-muted">
                          Enter odd number only (1, 3, 5, 7, 9, etc.)
                        </Form.Text>
                        {(errors.sets && formTouched.sets) && <div className="text-danger">{errors.sets}</div>}
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group className="mb-3">
                        <Form.Label>
                          Round
                          {isKnockout && <span style={{ color: 'red' }}>*</span>}
                        </Form.Label>
                        <Form.Control 
                          name="round" 
                          type="text"
                          value={form.round} 
                          onChange={handleInputChange} 
                          onBlur={() => setFormTouched({ ...formTouched, round: true })}
                          isInvalid={!!errors.round && formTouched.round} 
                          readOnly={viewMode}
                          placeholder="Enter round (e.g., Quarter Final, Semi Final)" 
                        />
                        <Form.Text className="text-muted">
                          {isKnockout ? 'Required for Knockout stage' : 'Optional: Enter round information'}
                        </Form.Text>
                        {(errors.round && formTouched.round) && <div className="text-danger">{errors.round}</div>}
                      </Form.Group>
                    </div>
                  </div>
                );
              })()}
              <div className="row">
                <div className="col-md-3">
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Group
                      {(() => {
                        const ageCategoryObj = ageCategories.find(ac => ac.value === form.ageCategory?.value);
                        const isGroupStandings = ageCategoryObj?.standingsType === 'group';
                        const isKnockout = form.stage?.value === 'knockout';
                        const shouldShowGroupField = isGroupStandings && !isKnockout;
                        return shouldShowGroupField ? <span style={{ color: 'red' }}>*</span> : null;
                      })()}
                    </Form.Label>
                    {(() => {
                      const ageCategoryObj = ageCategories.find(ac => ac.value === form.ageCategory?.value);
                      const isGroupStandings = ageCategoryObj?.standingsType === 'group';
                      const isKnockout = form.stage?.value === 'knockout';
                      const shouldShowGroupField = isGroupStandings && !isKnockout;
                      
                      if (shouldShowGroupField) {
                        return (
                          <>
                            <Select
                              classNamePrefix="react-select"
                              name="group"
                              value={form.group}
                              onChange={handleGroupChange}
                              options={availableGroups}
                              placeholder={groupsLoading ? "Loading groups..." : "Select Group"}
                              isClearable={false}
                              isLoading={groupsLoading}
                              isDisabled={viewMode || !form.ageCategory || groupsLoading}
                              styles={{ control: base => ({ ...base, minHeight: '38px' }) }}
                            />
                            {(errors.group && formTouched.group) && <div className="text-danger">{errors.group}</div>}
                          </>
                        );
                      } else {
                        return (
                          <Form.Control 
                            name="group" 
                            value="Not Required" 
                            readOnly
                            style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                            placeholder="Group not required for this age category"
                          />
                        );
                      }
                    })()}
                  </Form.Group>
                </div>
                {/* Knockout fields for Football - Show when stage is knockout */}
                {(() => {
                  const sportLower = getSportValue().toLowerCase();
                  const isBadmintonOrTT = sportLower === 'badminton' || sportLower === 'table tennis' || sportLower === 'tt';
                  const isKnockout = form.stage?.value === 'knockout';
                  
                  if (isBadmintonOrTT || !isKnockout) {
                    return null;
                  }
                  
                  return (
                    <>
                      <div className="col-md-3">
                        <Form.Group className="mb-3">
                          <Form.Label>Knockout Stage</Form.Label>
                          <Select
                            classNamePrefix="react-select"
                            name="knockoutStage"
                            value={form.knockoutStage}
                            onChange={option => handleSelectChange('knockoutStage', option)}
                            options={[
                              { value: TOURNAMENT_KNOCKOUT_STAGES.ELIMINATION, label: 'Elimination' },
                              { value: TOURNAMENT_KNOCKOUT_STAGES.QUALIFIER, label: 'Qualifier' },
                              { value: TOURNAMENT_KNOCKOUT_STAGES.QUARTERFINAL, label: 'Quarterfinal' },
                              { value: TOURNAMENT_KNOCKOUT_STAGES.SEMIFINAL, label: 'Semifinal' },
                              { value: TOURNAMENT_KNOCKOUT_STAGES.FINAL, label: 'Final' },
                              { value: TOURNAMENT_KNOCKOUT_STAGES.THIRDPLACE, label: 'Third Place' },
                            ]}
                            placeholder="Select Knockout Stage"
                            isClearable={true}
                            isDisabled={viewMode}
                            styles={{ control: base => ({ ...base, minHeight: '38px' }) }}
                          />
                        </Form.Group>
                      </div>
                      <div className="col-md-3">
                        <Form.Group className="mb-3">
                          <Form.Label>Knockout Index</Form.Label>
                          <Form.Control
                            name="knockoutIndex"
                            type="number"
                            value={form.knockoutIndex}
                            onChange={handleInputChange}
                            readOnly={viewMode}
                            placeholder="Enter index"
                          />
                        </Form.Group>
                      </div>
                      <div className="col-md-3">
                        <Form.Group className="mb-3">
                          <Form.Label>Knockout Match Number</Form.Label>
                          <Form.Control
                            name="knockoutMatchNum"
                            type="text"
                            value={form.knockoutMatchNum}
                            onChange={handleInputChange}
                            readOnly={viewMode}
                            placeholder="Enter match number"
                          />
                        </Form.Group>
                      </div>
                    </>
                  );
                })()}
                {[0, 1].map((index) => {
                  const usesPlayers = usesPlayerParticipants();
                  const isTeamSelectorDisabled = availableGroups.length > 0
                    && form.stage?.value !== 'knockout'
                    && !form.group;
                  const label = isFootball()
                    ? `Team ${index === 0 ? 'A' : 'B'}`
                    : `Participant ${index + 1}`;
                  return (
                    <div className="col-md-3" key={index}>
                      <Form.Group className="mb-3">
                        <Form.Label>{label}</Form.Label>
                        <Select
                          classNamePrefix="react-select"
                          name={`participant${index + 1}`}
                          value={form.participants?.[index]}
                          onChange={(option) => handleParticipantChange(index, option as SelectOption | SelectOption[])}
                          options={getParticipantOptions(index)}
                          placeholder={!form.ageCategory
                            ? 'Select Age Category first'
                            : usesPlayers
                              ? usesMultiPlayerSides()
                                ? `Select two players for Team ${index + 1}`
                                : `Select Participant ${index + 1}`
                              : isTeamSelectorDisabled
                                ? 'Select Group first'
                                : `Select ${label}`}
                          isMulti={usesMultiPlayerSides()}
                          isClearable={false}
                          isLoading={usesPlayers ? playersLoading : teamsLoading}
                          isDisabled={viewMode || !form.ageCategory || (usesPlayers ? playersLoading : isTeamSelectorDisabled)}
                          styles={{ control: base => ({ ...base, minHeight: '38px' }) }}
                        />
                        {((errors as Record<string, string>)[`participant${index + 1}`] && (formTouched as Record<string, boolean>)[`participant${index + 1}`]) && (
                          <div className="text-danger">{(errors as Record<string, string>)[`participant${index + 1}`]}</div>
                        )}
                      </Form.Group>
                    </div>
                  );
                })}
                <div className="col-md-3">
                  <Form.Group className="mb-3">
                    <Form.Label>Date<span style={{ color: 'red' }}>*</span></Form.Label>
                    <Form.Control 
                      name="date" 
                      type="date"
                      value={form.date} 
                      onChange={handleInputChange} 
                      isInvalid={!!errors.date && formTouched.date} 
                      onBlur={() => setFormTouched({ ...formTouched, date: true })}
                      readOnly={viewMode} 
                    />
                    {(errors.date && formTouched.date) && <div className="text-danger">{errors.date}</div>}
                  </Form.Group>
                </div>
              </div>
              <div className="row fixtures-form-row">
                <div className="col-md-5">
                  <Form.Group className="mb-3">
                    <Form.Label>Match Time<span style={{ color: 'red' }}>*</span></Form.Label>
                    <div className="time-picker-container">
                      {/* Custom Time Picker Button */}
                      <Button
                        ref={timePickerButtonRef}
                        variant="outline-secondary"
                        className="w-100 d-flex justify-content-between align-items-center time-picker-button"
                        onClick={openTimePicker}
                        disabled={viewMode}
                        style={{
                          height: '42px',
                          border: `2px solid ${errors.time && formTouched.time ? '#dc3545' : '#ced4da'}`,
                          backgroundColor: viewMode ? '#e9ecef' : 'white',
                          fontSize: '14px',
                          borderRadius: '8px',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <span className={form.time ? 'text-dark fw-medium' : 'text-muted'}>
                          {form.time || 'Select time'}
                        </span>
                        <i className="ri-time-line text-primary"></i>
                      </Button>
                      
                      {!viewMode && (
                        <small className="text-muted mt-1 d-block">
                          <i className="ri-information-line me-1"></i>
                          Click to select time
                        </small>
                      )}
                    </div>
                    {(errors.time && formTouched.time) && <div className="text-danger">{errors.time}</div>}
                  </Form.Group>
                </div>
                <div className="col-md-7">
                  <Form.Group className="mb-3">
                    <Form.Label>Venue<span style={{ color: 'red' }}>*</span></Form.Label>
                    <Form.Control 
                      name="venue" 
                      value={form.venue} 
                      onChange={handleInputChange} 
                      isInvalid={!!errors.venue && formTouched.venue} 
                      onBlur={() => setFormTouched({ ...formTouched, venue: true })}
                      readOnly={viewMode} 
                      placeholder="Enter venue name"
                    />
                    {(errors.venue && formTouched.venue) && <div className="text-danger">{errors.venue}</div>}
                  </Form.Group>
                </div>
              </div>
              <div className="row">
                <div className="col-md-12">
                  <Form.Group className="mb-3">
                    <Form.Label>Venue Full Address<span style={{ color: 'red' }}>*</span></Form.Label>
                    <Form.Control 
                      name="venueLong" 
                      value={form.venueLong} 
                      onChange={handleInputChange} 
                      isInvalid={!!errors.venueLong && formTouched.venueLong} 
                      onBlur={() => setFormTouched({ ...formTouched, venueLong: true })}
                      readOnly={viewMode}
                      placeholder="Enter full venue address" 
                    />
                    {(errors.venueLong && formTouched.venueLong) && <div className="text-danger">{errors.venueLong}</div>}
                  </Form.Group>
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Title</Form.Label>
                    <Form.Control
                      name="title"
                      value={form.title}
                      onChange={handleInputChange}
                      readOnly={viewMode}
                      placeholder="Enter match title"
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Subtitle</Form.Label>
                    <Form.Control
                      name="subtitle"
                      value={form.subtitle}
                      onChange={handleInputChange}
                      readOnly={viewMode}
                      placeholder="Enter match subtitle"
                    />
                  </Form.Group>
                </div>
              </div>
              <div className="row">
                <div className="col-md-12">
                  <Form.Group className="mb-3">
                    <Form.Label>Youtube Streaming URL</Form.Label>
                    <Form.Control
                      name="streamingVideoUrl"
                      value={form.streamingVideoUrl}
                      onChange={handleInputChange}
                      readOnly={viewMode}
                      placeholder="Enter Youtube streaming URL"
                    />
                  </Form.Group>
                </div>
              </div>
              <div className="d-flex justify-content-end gap-2">
                {viewMode ? (
                  <>
                    <Button variant="outline-secondary" onClick={handleClose}>
                      Close
                    </Button>
                    <Button 
                      variant="primary" 
                      onClick={() => setViewMode(false)}
                    >
                      Edit
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline-secondary" onClick={handleClose}>
                      Cancel
                    </Button>
                    <Button 
                      variant="primary" 
                      onClick={handleSave} 
                      disabled={!isFormValid || saving || !isFormModified}
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </Button>
                  </>
                )}
              </div>
            </Form>
          </Card.Body>
        </Card>
      )}

      {/* Fixtures Table */}
      <div className="manage-tournaments-table-container">
        <Table className="mb-0 manage-tournaments-table fixtures-table">
          <thead>
            <tr>
              <th>Match no</th>
              <th>Date / Time</th>
              <th>Status</th>
              <th>Age Categories</th>
              <th>Group</th>
              <th>Team A</th>
              <th>Team B</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {fixturesLoading ? (
              <tr>
                <td colSpan={8}><ShimmerLoader type="table" rows={5} /></td>
              </tr>
            ) : !fixtures || fixtures.length === 0 ? (
              <tr>
                <td colSpan={8}>
                  <div className="text-center py-5">
                    <div className="empty-state">
                      <div className="empty-state-icon">
                        <i className="ri-calendar-line" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
                      </div>
                      <div className="empty-state-title">No Fixtures Found</div>
                      <div className="empty-state-description">
                        There are no fixtures available for this tournament.
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              fixtures.map((fixture, index) => {
                // Format date
                const dateStr = fixture.date ? new Date(fixture.date).toLocaleDateString() : '-';
                
                // Only show time if it's available in the API response
                let dateTimeDisplay = dateStr;
                if (fixture.time && fixture.time.trim()) {
                  // Check if time is already in 12-hour format (contains AM/PM)
                  const isTime12Hour = fixture.time.includes('AM') || fixture.time.includes('PM');
                  const formattedTime = isTime12Hour ? fixture.time : formatTimeTo12Hour(fixture.time);
                  dateTimeDisplay = `${dateStr} ${formattedTime}`;
                }
                const participants = hydrateParticipants(fixture, isFootball());
                
                return (
                  <tr key={fixture._id}>
                    <td>{fixture.matchNum}</td>
                    <td>{dateTimeDisplay}</td>
                    <td>
                      {/* Priority: Use 'status' field first (primary field from API), then fallback to other fields */}
                      {getStatusBadge(fixture.status || (fixture as any)?.streamStatus || (fixture as any)?.liveStreamInfo?.status)}
                    </td>
                    <td>{typeof fixture.ageCategory === 'object' ? fixture.ageCategory?.ageCategory : '-'}</td>
                    <td>{fixture.group || '-'}</td>
                    <td>{getParticipantName(participants[0])}</td>
                    <td>{getParticipantName(participants[1])}</td>
                    <td>
                      <div className="tournament-actions">
                        {(() => {
                          const status = normalizeStatus(fixture.status);
                          const isLiveOrCompleted = status === 'started' || status === 'completed';
                          
                          // Edit button: Always enabled for navigation, but tooltip indicates editing is disabled on detail page
                          const editTitle = navigateToDetailPage 
                            ? (isLiveOrCompleted 
                                ? (status === 'started' ? 'View match details (editing disabled for live matches)' : 'View match details (editing disabled for completed matches)')
                                : 'Edit fixture')
                            : (isLiveOrCompleted 
                                ? (status === 'started' ? 'Cannot edit fixture when match is live' : 'Cannot edit fixture when match is completed')
                                : 'Edit');
                          
                          // Delete button: Always disabled for live/completed matches
                          const deleteTitle = isLiveOrCompleted
                            ? (status === 'started' ? 'Cannot delete fixture when match is live' : 'Cannot delete fixture when match is completed')
                            : 'Delete';
                          const deleteDisabledStyle = isLiveOrCompleted ? { opacity: 0.5, cursor: 'not-allowed' } : {};
                          
                          return (
                            <>
                              <button
                                className="action-btn btn-view"
                                onClick={() => handleView(index)}
                                title="View Fixture"
                                type="button"
                              >
                                <i className="ri-eye-line"></i>
                              </button>
                              {navigateToDetailPage && (
                                <button
                                  className="action-btn btn-view"
                                  onClick={() => handleOpenGallery(index)}
                                  title="Match Gallery"
                                  type="button"
                                >
                                  <i className="ri-image-2-line"></i>
                                </button>
                              )}
                              <button
                                className="action-btn btn-edit"
                                onClick={() => handleEdit(index)}
                                title={editTitle}
                                type="button"
                              >
                                <i className="ri-pencil-line"></i>
                              </button>
                              <button
                                className="action-btn btn-delete"
                                onClick={() => handleDelete(index)}
                                disabled={isLiveOrCompleted}
                                title={deleteTitle}
                                type="button"
                                style={deleteDisabledStyle}
                              >
                                <i className="ri-delete-bin-line"></i>
                              </button>
                            </>
                          );
                        })()}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-section mt-4" style={{ 
          padding: '16px 0',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div className="page-info mb-3">
            <span className="text-muted" style={{ 
              fontSize: '14px', 
              fontWeight: '500',
              color: '#6c757d'
            }}>
              Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalCount)} of {totalCount} fixtures
            </span>
          </div>
          <div className="pagination-controls d-flex align-items-center">
            <div className="page-numbers d-flex align-items-center">
              <Button 
                variant="outline-secondary" 
                size="sm" 
                onClick={() => onPageChange(page - 1)} 
                disabled={page === 1}
                style={{ marginRight: '8px' }}
              >
                Previous
              </Button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else {
                  const startPage = Math.max(1, Math.min(page - 2, totalPages - 4));
                  pageNum = startPage + i;
                }
                return (
                  <Button
                    key={pageNum}
                    variant={pageNum === page ? "primary" : "outline-secondary"}
                    size="sm"
                    onClick={() => onPageChange(pageNum)}
                    style={{ marginRight: '4px', minWidth: '32px' }}
                  >
                    {pageNum}
                  </Button>
                );
              })}
              <Button 
                variant="outline-secondary" 
                size="sm" 
                onClick={() => onPageChange(page + 1)} 
                disabled={page === totalPages}
                style={{ marginLeft: '8px' }}
              >
                Next
              </Button>
            </div>
            <div className="page-size-selector ms-4">
              <Form.Select 
                size="sm" 
                value={pageSize} 
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                style={{ minWidth: '80px' }}
              >
                {PAGE_SIZE_OPTIONS.map(size => (
                  <option key={size} value={size}>{size} per page</option>
                ))}
              </Form.Select>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        deleteModalFunction={confirmDelete}
        title="Delete Fixture"
        message="Are you sure you want to delete this fixture? This action cannot be undone."
      />

      {/* Bulk Upload Modal */}
      <Modal 
        show={showBulkUploadModal} 
        onHide={showResponseData ? undefined : handleBulkUploadClose} 
        size="xl" 
        centered
        backdrop={showResponseData ? 'static' : true}
        keyboard={!showResponseData}
      >
        <Modal.Header closeButton={!showResponseData}>
          <Modal.Title>Bulk Upload Fixtures</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {uploadSuccess ? (
            <div className="text-center py-4">
              <div className="text-success mb-3">
                <i className="ri-checkbox-circle-fill" style={{ fontSize: '3rem' }}></i>
              </div>
              <h5 className="text-success">Upload Successful!</h5>
              <p className="text-muted">Your fixtures have been uploaded successfully. The page will refresh automatically.</p>
            </div>
          ) : showResponseData ? (
            <div>
              <div className="text-center mb-4">
                <div className="text-warning mb-3">
                  <i className="ri-information-line" style={{ fontSize: '3rem' }}></i>
                </div>
                <h5 className="text-warning">Upload Completed with Issues</h5>
                <p className="text-muted">Please review the details below:</p>
              </div>

              {/* Response Data Display */}
              <div className="border rounded p-3 mb-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <h6 className="mb-3">Upload Results:</h6>
                
                {/* Handle the new API response structure: data.errors array */}
                {uploadResponseData?.data?.errors && Array.isArray(uploadResponseData.data.errors) && uploadResponseData.data.errors.length > 0 && (
                  <div className="mb-3">
                    <h6 className="text-danger mb-3">Issues Found ({uploadResponseData.data.errors.length}):</h6>
                    <div className="manage-tournaments-table-container">
                      <Table className="mb-0 manage-tournaments-table">
                        <thead>
                          <tr>
                            <th>Error Details</th>
                          </tr>
                        </thead>
                        <tbody>
                          {uploadResponseData.data.errors.map((error: string, index: number) => (
                            <tr key={index}>
                              <td>
                                <div className="d-flex align-items-start">
                                  <i className="ri-error-warning-line text-danger me-2 mt-1"></i>
                                  <span>{error}</span>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Handle legacy API response structure where errors are in data array */}
                {uploadResponseData?.data && Array.isArray(uploadResponseData.data) && uploadResponseData.data.length > 0 && !uploadResponseData.data.errors && (
                  <div className="mb-3">
                    <h6 className="text-danger mb-3">Issues Found ({uploadResponseData.data.length}):</h6>
                    <div className="manage-tournaments-table-container">
                      <Table className="mb-0 manage-tournaments-table">
                        <thead>
                          <tr>
                            <th>Error Details</th>
                          </tr>
                        </thead>
                        <tbody>
                          {uploadResponseData.data.map((error: string, index: number) => (
                            <tr key={index}>
                              <td>
                                <div className="d-flex align-items-start">
                                  <i className="ri-error-warning-line text-danger me-2 mt-1"></i>
                                  <span>{error}</span>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Show successful fixtures count if available */}
                {uploadResponseData?.data?.fixtures && Array.isArray(uploadResponseData.data.fixtures) && uploadResponseData.data.fixtures.length > 0 && (
                  <div className="mb-3">
                    <h6 className="text-success mb-2">Successfully Uploaded:</h6>
                    <div className="alert alert-success">
                      {uploadResponseData.data.fixtures.length} fixture(s) were successfully uploaded.
                    </div>
                  </div>
                )}


                {/* Legacy support for other response structures */}
                {uploadResponseData?.errors && uploadResponseData.errors.length > 0 && (
                  <div className="mb-3">
                    <h6 className="text-danger">Errors ({uploadResponseData.errors.length}):</h6>
                    <div className="alert alert-danger">
                      {uploadResponseData.errors.map((error: any, index: number) => (
                        <div key={index} className="mb-2">
                          <strong>Row {error.row || 'Unknown'}:</strong> {error.message || error.error || JSON.stringify(error)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {uploadResponseData?.warnings && uploadResponseData.warnings.length > 0 && (
                  <div className="mb-3">
                    <h6 className="text-warning">Warnings ({uploadResponseData.warnings.length}):</h6>
                    <div className="alert alert-warning">
                      {uploadResponseData.warnings.map((warning: any, index: number) => (
                        <div key={index} className="mb-2">
                          <strong>Row {warning.row || 'Unknown'}:</strong> {warning.message || warning.warning || JSON.stringify(warning)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {uploadResponseData?.successful && (
                  <div className="mb-3">
                    <h6 className="text-success">Successfully Processed:</h6>
                    <div className="alert alert-success">
                      {uploadResponseData.successful} fixtures were successfully uploaded.
                    </div>
                  </div>
                )}

                {/* Fallback for any other response structure */}
                {!uploadResponseData?.data && !uploadResponseData?.errors && !uploadResponseData?.warnings && !uploadResponseData?.successful && (
                  <div className="alert alert-info">
                    <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                      {JSON.stringify(uploadResponseData, null, 2)}
                    </pre>
                  </div>
                )}
              </div>

              <div className="text-center">
                <p className="text-muted">
                  <i className="ri-information-line me-1"></i>
                  You can upload a new file to fix the issues, or acknowledge the results to close this dialog.
                </p>
              </div>
            </div>
          ) : (
            <div>
              <div className="mb-3">
                <p className="text-muted">Upload an Excel file (.xlsx or .xls) to bulk add fixtures to this tournament.</p>
              </div>

              {/* Fixture Limit Warning */}
              <div className="alert alert-info d-flex align-items-start mb-3" role="alert">
                <i className="ri-information-line me-2 mt-1" style={{ fontSize: '1.2rem' }}></i>
                <div>
                  <strong>Upload Limit:</strong> You can upload a maximum of <strong>200 fixtures</strong> at a time. 
                  If your Excel file contains more than 200 fixtures, please split it into multiple files and upload them separately.
                </div>
              </div>

              {/* Download Template */}
              <div className="mb-4">
                <h6 className="mb-3">
                  <i className="ri-file-info-line me-2"></i>
                  Excel Template
                </h6>
                {(() => {
                  const currentSport = getSport().toLowerCase();
                  const isRacketSport = currentSport === 'tennis' || currentSport === 'badminton' || currentSport === 'table tennis' || currentSport === 'tabletennis' || currentSport === 'tt';
                  
                  // For racket sports, show both Singles and Doubles templates
                  if (isRacketSport) {
                    return (
                      <div>
                        <div className="row g-3 mb-3">
                          <div className="col-md-6">
                            <div className="d-flex align-items-center gap-2 mb-2">
                              <strong className="text-primary">Singles Format:</strong>
                            </div>
                            <a
                              href="https://docs.google.com/spreadsheets/d/1HdFPMQ11dvJeEjMeY73VCCFfzG9IOU82/edit?usp=sharing&ouid=110998283468080236376&rtpof=true&sd=true"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-outline-primary w-100"
                              style={{ textDecoration: 'none' }}
                            >
                              <i className="ri-download-line me-1"></i>
                              Download Singles Template
                            </a>
                            <small className="text-muted d-block mt-2">
                              <i className="ri-information-line me-1"></i>
                              Use for <strong>Singles matches</strong> (one player vs one player)
                            </small>
                          </div>
                          <div className="col-md-6">
                            <div className="d-flex align-items-center gap-2 mb-2">
                              <strong className="text-primary">Doubles Format:</strong>
                            </div>
                            <a
                              href="https://docs.google.com/spreadsheets/d/1QF5I6CryyXD_waXjO3nolGQ8SDdOsH1J/edit?usp=sharing&ouid=110998283468080236376&rtpof=true&sd=true"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-outline-primary w-100"
                              style={{ textDecoration: 'none' }}
                            >
                              <i className="ri-download-line me-1"></i>
                              Download Doubles Template
                            </a>
                            <small className="text-muted d-block mt-2">
                              <i className="ri-information-line me-1"></i>
                              Use for <strong>Doubles matches</strong> (two players vs two players)
                            </small>
                          </div>
                        </div>
                        <small className="text-muted d-block mt-2">
                          <i className="ri-information-line me-1"></i>
                          Download the appropriate template based on your match format, fill in your data, and upload the completed Excel file. Ensure team names, age categories, and groups match existing records.
                        </small>
                      </div>
                    );
                  }
                  
                  // For Football and other sports, show single template
                  const templateUrl = currentSport === 'football' 
                    ? "https://docs.google.com/spreadsheets/d/1baIS7HsIhW3oc7TKQGAz3QeMDYM5yPBrz7-nQhhhXz4/edit?gid=560880155#gid=560880155"
                    : "https://docs.google.com/spreadsheets/d/1baIS7HsIhW3oc7TKQGAz3QeMDYM5yPBrz7-nQhhhXz4/edit?gid=560880155#gid=560880155";
                  
                  return (
                    <div>
                      <div className="d-flex align-items-center gap-3">
                        <a
                          href={templateUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-primary"
                          style={{ textDecoration: 'none' }}
                        >
                          <i className="ri-download-line me-1"></i>
                          Download Template
                        </a>
                      </div>
                      <small className="text-muted d-block mt-2">
                        <i className="ri-information-line me-1"></i>
                        Download the template, fill in your data, and upload the completed Excel file. Ensure team names, age categories, and groups match existing records.
                      </small>
                    </div>
                  );
                })()}
              </div>

              {/* File Upload Area */}
              <div
                className={`border-2 border-dashed rounded p-4 text-center ${
                  uploadFile ? 'border-success bg-light' : 'border-secondary'
                }`}
                onDrop={handleFileDrop}
                onDragOver={handleDragOver}
                style={{ minHeight: '200px', cursor: 'pointer' }}
                onClick={() => document.getElementById('fileInput')?.click()}
              >
                <input
                  id="fileInput"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                
                {uploadFile ? (
                  <div>
                    <div className="text-success mb-2">
                      <i className="ri-file-excel-2-fill" style={{ fontSize: '2rem' }}></i>
                    </div>
                    <h6 className="text-success">{uploadFile.name}</h6>
                    <p className="text-muted mb-0">File selected successfully</p>
                  </div>
                ) : (
                  <div>
                    <div className="text-muted mb-3">
                      <i className="ri-upload-cloud-2-line" style={{ fontSize: '3rem' }}></i>
                    </div>
                    <h5>Drop your Excel file here</h5>
                    <p className="text-muted">or click to browse files</p>
                    <small className="text-muted">Supports .xlsx and .xls files</small>
                  </div>
                )}
              </div>

              {/* Upload Progress */}
              {uploading && (
                <div className="mt-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span>Uploading...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="progress">
                    <div
                      className="progress-bar progress-bar-striped progress-bar-animated"
                      role="progressbar"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {uploadError && (
                <div className="alert alert-danger mt-3">
                  <div className="d-flex align-items-start">
                    <i className="ri-error-warning-line me-2 mt-1"></i>
                    <div>
                      <strong>Upload Failed</strong>
                      <div className="mt-1">{uploadError}</div>
                      <small className="text-muted d-block mt-2">
                        {uploadError.includes('File processing error') ? (
                          <>
                            <strong>Possible solutions:</strong><br/>
                            • Ensure your Excel file is not corrupted<br/>
                            • Try saving the file in a different Excel format (.xlsx)<br/>
                            • Check that the file contains valid data in all required columns<br/>
                            • Make sure the file is not password protected
                          </>
                        ) : (
                          'Please check your file format and try again. If the problem persists, contact support.'
                        )}
                      </small>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {showResponseData ? (
            <>
              <Button variant="outline-secondary" onClick={handleReupload}>
                <i className="ri-upload-line me-1"></i>
                Upload New File
              </Button>
              <Button variant="primary" onClick={handleAcknowledgeResponse}>
                <i className="ri-check-line me-1"></i>
                Acknowledge & Close
              </Button>
            </>
          ) : !uploadSuccess && (
            <>
              <Button variant="secondary" onClick={handleBulkUploadClose} disabled={uploading}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleBulkUpload}
                disabled={!uploadFile || uploading}
              >
                {uploading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Uploading...
                  </>
                ) : (
                  'Upload Fixtures'
                )}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      <TournamentBulkUploadModal
        show={showTournamentBulkUpload}
        onHide={() => setShowTournamentBulkUpload(false)}
        onSuccess={onAdd}
        resource="fixtures"
        tournamentId={tournamentId}
        tournamentSeasonId={tournamentSeasonId}
        sport={tournamentSports}
      />

      {/* Custom Time Picker Dropdown - Portal Rendered */}
      {showTimePicker && createPortal(
        <>
          {/* Backdrop */}
          <div 
            className="time-picker-backdrop"
            onClick={closeTimePicker}
          ></div>
          
          <div 
            className="custom-time-picker-dropdown"
            style={{
              top: `${pickerPosition.top}px`,
              left: `${pickerPosition.left}px`
            }}
          >
            <div className="time-picker-header">
              <small className="text-muted fw-medium">Select Time</small>
              <button
                type="button"
                className="btn-close btn-sm"
                onClick={closeTimePicker}
              ></button>
            </div>
            
            <div className="time-picker-content">
              {/* Hour Selection */}
              <div className="time-picker-section">
                <small className="text-muted">Hour</small>
                <div className="time-options">
                  {hours.map((hourOption) => (
                    <button
                      key={hourOption.value}
                      type="button"
                      className={`time-option ${selectedHour === hourOption.value ? 'active' : ''}`}
                      onClick={() => handleCustomTimeSelection('hour', hourOption.value)}
                    >
                      {hourOption.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Minute Selection */}
              <div className="time-picker-section">
                <small className="text-muted">Minute</small>
                <div className="time-options">
                  {minutes.map((minuteOption) => (
                    <button
                      key={minuteOption.value}
                      type="button"
                      className={`time-option ${selectedMinute === minuteOption.value ? 'active' : ''}`}
                      onClick={() => handleCustomTimeSelection('minute', minuteOption.value)}
                    >
                      {minuteOption.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* AM/PM Selection */}
              <div className="time-picker-section">
                <small className="text-muted">Period</small>
                <div className="time-options">
                  {amPmOptions.map((amPmOption) => (
                    <button
                      key={amPmOption.value}
                      type="button"
                      className={`time-option ampm-option ${selectedAmPm === amPmOption.value ? 'active' : ''}`}
                      onClick={() => handleCustomTimeSelection('amPm', amPmOption.value)}
                      disabled={!selectedHour || !selectedMinute}
                    >
                      {amPmOption.label}
                    </button>
                  ))}
                </div>
              </div>
              
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default FixturesTable;
